import { CurrencyPipe, DatePipe } from '@angular/common';
import { Injectable, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import * as FileSaver from 'file-saver';
import { forEach } from 'lodash';
import { ngxCsv } from 'ngx-csv/ngx-csv';
import * as pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import { Observable, Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';

import { dateConfig } from '@app/core/_config';
import { ClientPurchaseOrderStatusTextConstant } from '@app/core/_constant/b2b-clients.constants';
import { ClientPurchaseOrderDiscountCodesConstant } from '@app/core/_constant/index.constants';
import { SettingsKey } from '@app/core/_constant/settings.constants';
import { PermissionService } from '@app/core/auth/services/permission.service';
import { ClientOrderDatesService } from '@app/core/client/utils/client-order-dates.service';
import { PoService } from '@app/core/purchase-orders/services/po.service';
import { SettingsService } from '@app/core/settings/services/settings.service';
import { environment } from '@app/env-service';
import { UnitMeasureInterface } from '@app/modules/unit-measurement/interfaces';
import { DecimalPlacesPipe } from 'v2/modules/shared/pipes/decimal-places.pipe';

import { FeatureToggleService } from './feature-toggle.service';

pdfMake.vfs = pdfFonts.pdfMake.vfs;

@Injectable({
  providedIn: 'root',
})
export class ExportSkuVaultService implements OnDestroy {
  url :string = environment.url;

  curPipe :CurrencyPipe;
  datePipe :DatePipe;
  decimalPlacesPipe :DecimalPlacesPipe;
  purchaseOrders :any[] = [];
  profileSettings :string[] = [];
  showSupplierPoSourceSkuSetting :boolean = false;
  showSupplierPoDescriptionSetting :boolean = false;
  settingsKey :typeof SettingsKey = SettingsKey;
  private onDestroy$ = new Subject();

  paymentTypes :any[] = [
    { id: 1, text: 'wire transfer' },
    { id: 20, text: 'cash' },
    { id: 30, text: 'ach' },
    { id: 40, text: 'credit card' },
    { id: 50, text: 'debit card' },
    { id: 60, text: 'check' },
  ];

  clientConfig :any = environment.clientConfig;

  excelFileType =
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
  excelFileExtension = '.xlsx';

  constructor(
    private poSvc :PoService,
    private UserPermission :PermissionService,
    private clientOrderDatesService :ClientOrderDatesService,
    private translate :TranslateService,
    private settingsSvc :SettingsService,
    private ft :FeatureToggleService,
  ) {
    this.curPipe = new CurrencyPipe('en');
    this.datePipe = new DatePipe('en');
    this.decimalPlacesPipe = new DecimalPlacesPipe();
    this.getMyProfileSettings().subscribe();
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  private saveExcelFile(buffer :any, fileName :string, ext ?:string) :void {
    const data :Blob = new Blob([buffer], { type: this.excelFileType });
    FileSaver.saveAs(data, fileName + (ext ? ext : this.excelFileExtension));
  }

  exportPurchaseOrdersCSV(purchaseOrders :any[]) {
    const data = [];

    purchaseOrders.forEach((po :any) => {
      const poNumber :string = po.id.toString();
      let orderDate :any = ' ';
      try {
        orderDate = new Date(po.arrival_due_date);
        orderDate = this.datePipe.transform(orderDate, dateConfig.defaultDatePipe);
      } catch (e) {
        orderDate = ' ';
      }
      let arrivalDueDate :any = ' ';
      try {
        arrivalDueDate = new Date(po.arrival_due_date);
        arrivalDueDate = this.datePipe.transform(
          arrivalDueDate,
          dateConfig.defaultDatePipe,
        );
      } catch (e) {
        arrivalDueDate = ' ';
      }
      let requestedShipDate :any = ' ';
      try {
        requestedShipDate = new Date(po.arrival_due_date);
        requestedShipDate = this.datePipe.transform(
          requestedShipDate,
          dateConfig.defaultDatePipe,
        );
      } catch (e) {
        requestedShipDate = ' ';
      }

      po.purchase_order_items.forEach((item) => {
        const purchaseOrder = {
          po_no: poNumber,
          supplier: po.factory.factory_name,
          itemSearchItem: item.internal_sku.length > 3 ? item.internal_sku : '',
          itemOrderedQty: item.quantity,
          itemCost: item.unit_price, //item.line_total,
          groupingId: '', //item.rfq_id,
          orderedDate: orderDate,
          terms: po.payment_terms || '',
          orderCancelDate: '',
          shippingCarrier: (po.logistic_label || '').toUpperCase(),
          shippingClass: (po.logistic_label || '').toUpperCase(),
          requestedShipDate: requestedShipDate,
          arrivalDueDate: arrivalDueDate,
          trackingInfo: '',
          publicNote: po.public_note || '',
          privateNote: po.private_note || '',
          sentStatus: 'Not Sent',
          paymentStatus:
            po.total_paid > 0
              ? po.total_paid >= po.total_cost
                ? 'Fully Paid'
                : 'Partially Paid'
              : 'None Paid',
          shipToWarehouse: 'QUALHQ',
          shipToAddressName: environment.clientConfig.companyName,
          itemOrderedQty3PL: '0',
          itemPublicNote: item.public_note || '',
          itemPrivateNote: item.private_note || '',
          itemIdentifier: '',
          itemVariant: '',
          costType: '', //po.cost_type || "",
          costAmount: '', //item.line_total,
          costNote: '', //po.cost_note || "",
        };

        data.push(purchaseOrder);
      });
    });

    const headers :string[] = [
      'PO#',
      'Supplier',
      'Item Search Term',
      'Item Ordered Quantity',
      'Item Cost',
      'Grouping ID',
      'Ordered Date',
      'Terms',
      'Order Cancel Date',
      'Shipping Carrier',
      'Shipping Class',
      'Requested Ship Date',
      'Arrival Due Date',
      'Tracking Info',
      'Public Note',
      'Private Note',
      'Sent Status',
      'Payment Status',
      'Ship To Warehouse',
      'Ship To Address Name',
      'Item Ordered Quantity To 3PL',
      'Item Public Note',
      'Item Private Note',
      'Item Identifier',
      'Item Variant',
      'Cost Type',
      'Cost Amount',
      'Cost Note',
    ];

    const options = {
      fieldSeparator: ',',
      quoteStrings: '"',
      decimalseparator: '.',
      showLabels: true,
      showTitle: false,
      title: 'Channics Purchase Order',
      useBom: true,
      noDownload: false,
      headers: headers,
    };

    const filename = 'channicsPurchaseOrder';

    Swal.close();

    new ngxCsv(data, filename, options);
  }

  exportPOtoPDF(purchaseOrder :any) {
    const logo = environment.clientConfig.assetImgDir + 'logo-print-new.png';
    let temp_logo = null;
    try {
      temp_logo = purchaseOrder.companyLogo;
    } catch (e) {}
    if (
      (temp_logo && temp_logo.startsWith('data:image/webp;base64,')) ||
      (temp_logo && temp_logo.startsWith('data:image/png;base64,'))
    ) {
      // Process and then create PDF
      this.processPNG(temp_logo, (convertedLogo :any) => {
        this.creatPDF(purchaseOrder, convertedLogo);
      });
    } else {
      // If temp_logo is not set, use the default logo
      this.getImgDataUrl(logo, (imgUri :any) => {
        this.creatPDF(purchaseOrder, imgUri);
      });
    }
  }

  processPNG(dataUrl :string, callback :(pngUrl :string) => void) :void {
    const img = new Image();
    img.onload = function () {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);

      // Convert to PNG or JPEG format; here we're using PNG
      const dataUrl = canvas.toDataURL('image/png');
      callback(dataUrl);
    };
    img.src = dataUrl;
  }

  private getMyProfileSettings() :Observable<any> {
    return this.settingsSvc.getMyProfileSettings().pipe(
      tap((data :any) => {
        if (data && data.value) {
          this.profileSettings = JSON.parse(data.value);
        }
      }),
      takeUntil(this.onDestroy$),
    );
  }

  async creatPDF(purchaseOrder, logoUri) {
    const tableData :any[] = [
      [
        { text: 'Line', bold: true },
        { text: 'Item No.', bold: true },
        { text: 'Product Name', bold: true },
        { text: 'Unit', bold: true },
        { text: 'Quantity', alignment: 'right', bold: true },
        { text: 'Unit Cost', alignment: 'right', bold: true },
        { text: 'Total Cost', alignment: 'right', bold: true },
      ],
    ];

    if (
      !this.ft.featureEnabled('red_DV-2659_fifo_landed_cost_multiple_shipments')
    ) {
      tableData[0].splice(6, 0, {
        text: 'Downpayment',
        alignment: 'right',
        bold: true,
      });
    }

    try {
      this.showSupplierPoSourceSkuSetting = await this.settingsSvc
        .getCachedKeyValueSettings(this.settingsKey.SHOW_SUPPLIER_PO_SOURCE_SKU)
        .toPromise();
    } catch (error) {
      console.error(error);
    }

    try {
      this.showSupplierPoDescriptionSetting = await this.settingsSvc
        .getCachedKeyValueSettings(this.settingsKey.SHOW_SUPPLIER_PO_DESCRIPTION)
        .toPromise();
    } catch (error) {
      console.error(error);
    }

    if (
      this.showSupplierPoSourceSkuSetting ||
      this.showSupplierPoDescriptionSetting
    ) {
      // Only remove 'Product Name' if either of the settings is false
      tableData[0].splice(2, 1);
    }

    if (this.showSupplierPoSourceSkuSetting) {
      tableData[0].splice(2, 0, { text: 'Source Sku', bold: true });
    }

    if (this.showSupplierPoDescriptionSetting) {
      // Adjust the index based on whether 'Source Sku' has been inserted or not
      const index = !this.showSupplierPoSourceSkuSetting ? 2 : 3;
      tableData[0].splice(index, 0, { text: 'Description', bold: true });
    }

    let counter :number = 0;
    let totalUnits :number = 0;

    purchaseOrder.purchase_order_items.forEach((item, idx) => {
      counter++;
      const orderedQtyNumber = parseFloat(item.ordered_qty);
      totalUnits += orderedQtyNumber;
      tableData.push([
        { text: counter, margin: [0, 3, 0, 3] },
        { text: item.internal_sku, margin: [0, 3, 0, 3] },
        {
          text: item.product_name,
          margin: [0, 3, 0, 3],
        },
        {
          text: item.ordered_unit_of_measure
            ? item.ordered_unit_of_measure?.name
            : '',
          margin: [0, 3, 0, 3],
        },
        {
          text: this.decimalPlacesPipe.transform(item.quantity),
          alignment: 'right',
          margin: [0, 3, 0, 3],
        },
        {
          text: this.curPipe.transform(item.unit_price),
          alignment: 'right',
          margin: [0, 3, 0, 3],
        },
        {
          text: this.curPipe.transform(item.line_total),
          alignment: 'right',
          margin: [0, 3, 0, 3],
        },
      ]);
      // Initially remove 'Product Name' only if either setting is false
      if (
        this.showSupplierPoSourceSkuSetting ||
        this.showSupplierPoDescriptionSetting
      ) {
        tableData[counter].splice(2, 1);
      }

      // If Source Sku is not disabled, insert it
      if (this.showSupplierPoSourceSkuSetting) {
        tableData[counter].splice(2, 0, {
          text: item.source_sku || '',
          margin: [0, 3, 0, 3],
        });
      }

      // If Description is not disabled, insert it
      if (this.showSupplierPoDescriptionSetting) {
        // Adjust the index based on whether 'Source Sku' has been inserted or not
        const index = !this.showSupplierPoSourceSkuSetting ? 2 : 3;
        tableData[counter].splice(index, 0, {
          text: `[${item.product_name}] \n` + (item?.description || ''),
        });
      }

      if (
        !this.ft.featureEnabled('red_DV-2659_fifo_landed_cost_multiple_shipments')
      ) {
        // insert this to index 7
        tableData[counter].splice(7, 0, {
          text: this.curPipe.transform(item.downpayment_amount),
          alignment: 'right',
          margin: [0, 3, 0, 3],
        });
      }
    });

    purchaseOrder.purchase_order_custom_items.forEach((item, idx) => {
      counter++;
      tableData.push([
        { text: counter, margin: [0, 3, 0, 3] },
        { text: '', margin: [0, 3, 0, 3] },
        {
          text: item.product_name,
          margin: [0, 3, 0, 3],
        },
        { text: item.unit || 'units', margin: [0, 3, 0, 3] },
        {
          text: this.decimalPlacesPipe.transform(item.quantity),
          alignment: 'right',
          margin: [0, 3, 0, 3],
        },
        {
          text: this.curPipe.transform(item.unit_price),
          alignment: 'right',
          margin: [0, 3, 0, 3],
        },
        {
          text: this.curPipe.transform(item.line_total),
          alignment: 'right',
          margin: [0, 3, 0, 3],
        },
      ]);

      // Initially remove 'Product Name' only if either setting is false
      if (
        this.showSupplierPoSourceSkuSetting ||
        this.showSupplierPoDescriptionSetting
      ) {
        tableData[counter].splice(2, 1);
      }

      // If Source Sku is not disabled, insert it
      if (this.showSupplierPoSourceSkuSetting) {
        tableData[counter].splice(2, 0, {
          text: item.source_sku || '',
          margin: [0, 3, 0, 3],
        });
      }

      // If Description is not disabled, insert it
      if (this.showSupplierPoDescriptionSetting) {
        // Adjust the index based on whether 'Source Sku' has been inserted or not
        const index = !this.showSupplierPoSourceSkuSetting ? 2 : 3;
        tableData[counter].splice(index, 0, {
          text: `[${item.product_name}] \n` + (item?.description || ''),
        });
      }

      if (
        !this.ft.featureEnabled('red_DV-2659_fifo_landed_cost_multiple_shipments')
      ) {
        // insert this to index 7
        tableData[counter].splice(7, 0, {
          text: this.curPipe.transform(item.downpayment_amount),
          alignment: 'right',
          margin: [0, 3, 0, 3],
        });
      }
    });

    const poNumber :string = purchaseOrder.id.toString();
    // prepare header
    const orderDate :string =
      this.clientOrderDatesService.transformToDatetimeString(
        purchaseOrder.order_date,
        dateConfig.defaultDatePipe,
      );
    const alternateIds :string =
      purchaseOrder.alternate_ids?.length > 0
        ? purchaseOrder.alternate_ids
            .map((alternateId) => alternateId.alternate_id)
            .join(', ')
        : '';

    const headerContent = [
      {
        columns: [
          {
            alignment: 'left',
            image: logoUri,
            width: 130,
            style: 'logo',
          },
          [
            {
              text: 'Purchase Order',
              alignment: 'right',
              style: 'poheaderright',
            },
            // { text: 'Page: ' + currentPage },
            { text: ' ' },
            {
              text: 'Purchase Order:     ' + poNumber,
              alignment: 'right',
            },
            {
              text: 'Purchase Order Date:    ' + purchaseOrder.order_date,
              alignment: 'right',
            },
          ],
        ],
      },
    ];

    let totalShipping :number = 0;
    if (
      purchaseOrder.purchase_order_shipping_costs &&
      Array.isArray(purchaseOrder.purchase_order_shipping_costs) &&
      purchaseOrder.purchase_order_shipping_costs.length > 0
    ) {
      totalShipping = purchaseOrder.purchase_order_shipping_costs.reduce(
        (p, c) => p + c.cost_amount,
        0,
      );
    }
    const footerContent = [
      {
        columns: [
          { text: 'Total Units:', alignment: 'right' },
          {
            text: totalUnits,
            alignment: 'right',
            width: 60,
          },
        ],
      },
      {
        columns: [
          { text: 'Order Cost:', alignment: 'right' },
          {
            text: this.curPipe.transform(purchaseOrder.total_cost),
            alignment: 'right',
            width: 60,
          },
        ],
      },
      {
        columns: [
          { text: 'Shipping Cost:', alignment: 'right' },
          {
            text: this.curPipe.transform(totalShipping),
            alignment: 'right',
            width: 60,
          },
        ],
      },
      {
        columns: [
          { text: 'Total Cost:', alignment: 'right' },
          {
            text: this.curPipe.transform(purchaseOrder.total_cost + totalShipping),
            alignment: 'right',
            style: 'warning',
            width: 60,
          },
        ],
      },
      {
        columns: [
          { text: 'Total Paid:', alignment: 'right' },
          {
            text: this.curPipe.transform(purchaseOrder.total_paid),
            alignment: 'right',
            style: 'info',
            width: 60,
          },
        ],
      },
      {
        columns: [
          { text: 'Balance Payable:', alignment: 'right' },
          {
            text: this.curPipe.transform(
              purchaseOrder.total_cost + totalShipping - purchaseOrder.total_paid,
            ),
            alignment: 'right',
            style: 'danger',
            bold: true,
            width: 60,
          },
        ],
      },
    ];

    let shipTo = null;

    if (purchaseOrder.default_ship_to_address == 'ship_to_address') {
      shipTo = [
        purchaseOrder.street_address_1,
        purchaseOrder.street_address_2,
        purchaseOrder.city,
        purchaseOrder.state,
        purchaseOrder.zip,
        purchaseOrder.country,
      ]
        .filter(Boolean)
        .map((elem) => elem.replace(/\s+/g, ' ').trim())
        .join(', ');
    } else if (purchaseOrder.default_ship_to_address == 'warehouse') {
      if (purchaseOrder.warehouse != null) {
        shipTo = [
          purchaseOrder.warehouse.street_address_1,
          purchaseOrder.warehouse.street_address_2,
          purchaseOrder.warehouse.city,
          purchaseOrder.warehouse.state,
          purchaseOrder.warehouse.zip,
          purchaseOrder.warehouse.country,
        ]
          .filter(Boolean)
          .map((elem) => elem.replace(/\s+/g, ' ').trim())
          .join(', ');
      }
    }

    const widths = [20, 45, '*', 'auto', 'auto', 'auto', 'auto'];
    if (
      !this.ft.featureEnabled('red_DV-2659_fifo_landed_cost_multiple_shipments')
    ) {
      widths.splice(6, 0, 'auto');
    }

    if (
      this.showSupplierPoSourceSkuSetting &&
      this.showSupplierPoDescriptionSetting
    ) {
      widths.splice(2, 0, 'auto');
    }

    const tableContent :any[] = [
      {
        style: 'shipping',
        columns: [
          [
            { text: 'FROM: ' },
            {
              text: this.profileSettings['company_name'],
              bold: true,
            },
            {
              text: this.profileSettings['company_address'],
            },
          ],
          [
            { text: 'TO: ' },
            {
              text: purchaseOrder.factory?.factory_name || '',
              bold: true,
            },
            {
              text: [
                purchaseOrder.factory?.street_address || '',
                purchaseOrder.factory?.city || '',
                purchaseOrder.factory?.state || '',
                purchaseOrder.factory?.country || '',
              ]
                .filter((item) => item)
                .join(', '),
            },
          ],
        ],
      },
      {
        style: 'shipping',
        columns: [
          [{ text: 'SHIP TO ADDRESS:' }, { text: shipTo ?? 'N/A' }],
          [{ text: '' }],
        ],
      },
      {
        style: 'shipping',
        columns: [
          [
            { text: 'Ship via: ' + purchaseOrder.logistic_label },
            { text: 'Shipping agent: ' },
            {
              text: 'Payment Terms: ' + purchaseOrder.payment_terms,
            },
          ],
          [
            {
              text:
                'Requested Ship Date: ' +
                (this.datePipe.transform(
                  purchaseOrder.requested_ship_date,
                  dateConfig.defaultDatePipe,
                ) || ''),
            },
            {
              text:
                'Estimated Arrival: ' +
                (this.datePipe.transform(
                  purchaseOrder.arrival_due_date,
                  dateConfig.defaultDatePipe,
                ) || ''),
            },
            {
              text:
                'Note: ' +
                (purchaseOrder.public_note ? purchaseOrder.public_note : ''),
            },
          ],
        ],
      },
      {
        style: 'shipping',
        columns: [[{ text: 'Alternate IDs: ' + alternateIds }]],
      },

      { text: 'Order Items', alignment: 'center', style: 'header' },
      {
        style: 'table',
        table: {
          widths: widths,
          headerRows: 1,
          body: tableData,
        },
        layout: {
          fillColor: function (i :number) {
            return i % 2 === 1 ? '#f4f4f4' : i > 1 ? '#eceefa' : null;
          },
          hLineWidth: function (i :number) {
            return i === 1 ? 1 : 0;
          },
          vLineWidth: function (i :number) {
            return 0;
          },
        },
      },
    ];

    let pdfContent :any[] = [];

    pdfContent = pdfContent.concat(headerContent, tableContent, footerContent);

    const documentDefinition = {
      pageSize: 'letter',
      pageOrientation: 'portrait',

      content: pdfContent,

      defaultStyle: {
        fontSize: 9,
        columnGap: 16,
      },
      styles: {
        poheaderright: {
          margin: [0, 25, 0, 0],
          fontSize: 18,
          fillColor: '#00ee00',
        },
        logo: {
          fillColor: '#ee0000',
        },
        summarytitle: {
          fontSize: 14,
          bold: true,
        },
        shipping: {
          margin: [0, 25, 0, 0],
        },
        header: {
          fontSize: 16,
          bold: false,
          margin: [0, 15, 0, 5],
          padding: [0, 0, 0, 0],
        },
        subheader: {
          fontSize: 14,
          bold: false,
          margin: [0, 0, 0, 0],
          padding: [0, 0, 0, 0],
        },
        table: {
          margin: [0, 5, 0, 15],
          fontSize: 9,
        },
        tableHeader: {
          bold: true,
          fontSize: 9,
          color: 'black',
        },
        warning: {
          color: '#ffb822',
        },
        info: {
          color: '#5578eb',
        },
        danger: {
          color: '#FF0000',
        },
      },
    };

    Swal.close();
    pdfMake
      .createPdf(documentDefinition)
      .download('PurchaseOrder_' + poNumber + '.pdf');
  }

  getImgDataUrl(imgUrl :string, callback) {
    const xhr = new XMLHttpRequest();
    xhr.onload = function () {
      const reader = new FileReader();
      reader.onloadend = function () {
        callback(reader.result);
      };
      reader.readAsDataURL(xhr.response);
    };
    xhr.open('GET', imgUrl);
    xhr.responseType = 'blob';
    xhr.send();
  }

  exportPurchaseOrderstoPDF(
    purchaseOrders :any[],
    title :string,
    export_type ?:any,
  ) {
    // const logoUri = await this.getImgDataUrl('/assets/img/logo-print.png');
    this.getImgDataUrl(
      environment.clientConfig.assetImgDir + 'logo-print.png',
      (imgUri :any) => {
        this.createPurchaseOrdersPDF(purchaseOrders, title, imgUri, export_type);
      },
    );
  }

  createPurchaseOrdersPDF(
    purchaseOrders :any[],
    title,
    logoUri,
    export_type ?:any,
  ) {
    // prepare table data
    let tableData :any[] = [
      [
        { text: 'Order Date', bold: true },
        // { text: "Products [Qty Received/Ordered, Unit Cost Landed/Ordered]", bold: true },
        { text: 'Products', bold: true },
        // { text: "Qty Received/Ordered", bold: true },
        // { text: "Unit Cost Landed/Ordered", bold: true },
        { text: 'Units Received', alignment: 'right', bold: true },
        { text: 'Order Quantity', alignment: 'right', bold: true },
        { text: 'Estimated Arrival / Incoterm', alignment: 'right', bold: true },
        { text: 'Total Order Cost', alignment: 'right', bold: true },
        { text: 'Total Paid', alignment: 'right', bold: true },
      ],
    ];
    if (export_type && export_type == 10) {
      tableData = [
        [
          { text: 'Order Date', bold: true },
          { text: 'Products', bold: true },
          // { text: "Qty Received/Ordered", bold: true },
          // { text: "Unit Cost Landed/Ordered", bold: true },
          {
            text: 'Units Received',
            alignment: 'right',
            bold: true,
          },
          {
            text: 'Receivable Quantity',
            alignment: 'right',
            bold: true,
          },
          { text: 'Receivable Cost', alignment: 'right', bold: true },
          { text: 'Total Paid', alignment: 'right', bold: true },
        ],
      ];
    }

    if (!this.UserPermission.can('exportUnitCost')) {
      tableData[0].splice(4, 1);
    }

    // purchaseOrders.forEach((po, idx) => {
    // });
    for (let idx = 0; idx < purchaseOrders.length; idx++) {
      const po :any = purchaseOrders[idx];

      let quantity = po.order_quantity;
      const unitsReceived = po.total_received_rfqs
        ? po.total_received_rfqs.quantity
        : 0;
      let receivable_cost = po.total_cost;
      if (po.total_shipping_cost == 0) {
        po.total_shipping_cost = po.landed_domestic + po.landed_intl;
      }

      if (export_type && export_type == 10) {
        if (po.total_received_rfqs) {
          // check if paid in full
          if (po.total_paid >= po.total_cost) {
            quantity = quantity - po.total_received_rfqs.quantity;
            receivable_cost = po.total_cost - po.total_received_rfqs.line_total;
          } else {
            if (po.total_paid <= po.total_received_rfqs.line_total) {
              continue;
            }
            receivable_cost = po.total_paid - po.total_received_rfqs.line_total;
            const receivableQty :any = quantity * (po.total_paid / po.total_cost);
            const toReceivedQty =
              receivableQty * (po.total_received_rfqs.line_total / po.total_paid);
            quantity = this.curPipe.transform(toReceivedQty, ' ', false, '1.0-0');
          }
        } else {
          receivable_cost = po.total_paid;
          const receivableQty :any = quantity * (po.total_paid / po.total_cost);
          quantity = this.curPipe.transform(receivableQty, ' ', false, '1.0-0'); //receivableQty.toString() + '/' + quantity.toString() + "*";
        }
      }
      if (quantity < 0) {
        quantity = 0;
      }
      if (receivable_cost < 0) {
        receivable_cost = 0;
      }

      // po.items_list = this.poSvc.getItemsTextsToRowsArray(po);

      const rowItems :any[] = [
        [
          'SKU',
          'Product Name',
          'Qty Received/Ordered',
          'Date Received',
          'Unit Cost Landed/Ordered',
        ],
      ];

      if (!this.UserPermission.can('exportUnitCost')) {
        rowItems[0].splice(4, 1);
      }

      po.purchase_order_items.forEach((item) => {
        const receivedQty :number = item.received_entries
          ? item.received_entries.quantity
          : 0;
        const receivedDate :any =
          item.received_entries && item.received_entries.receiving_report
            ? this.datePipe.transform(
                item.received_entries.receiving_report.received_date,
                dateConfig.defaultDatePipe,
              )
            : '';
        const landedCost :number =
          item.unit_price +
          (((item.unit_price * item.quantity) / po.total_cost) *
            po.total_shipping_cost) /
            item.quantity;

        const rowItem :any[] = [
          item.internal_sku &&
          item.internal_sku != 'N/A' &&
          item.internal_sku != 'null'
            ? '(' + item.internal_sku + ') '
            : '',
          item.product_name,
          receivedQty + '/' + item.quantity,
          receivedDate,
          this.curPipe.transform(landedCost) +
            '/' +
            this.curPipe.transform(item.unit_price),
        ];

        if (!this.UserPermission.can('exportUnitCost')) {
          rowItem.splice(4, 1);
        }

        rowItems.push(rowItem);
      });

      const widths = ['auto', '*', 'auto', 'auto', 'auto'];
      if (!this.UserPermission.can('exportUnitCost')) {
        widths.splice(4, 1);
      }

      const orderDate :string =
        this.clientOrderDatesService.transformToDatetimeString(
          po.order_date,
          dateConfig.defaultDatePipe,
        );
      let estArrivalEcoterm = po?.logistic_label;
      if (po?.arrival_due_date) {
        const arrivalDueDate = this.datePipe.transform(
          po?.arrival_due_date,
          dateConfig.defaultDatePipe,
        );
        estArrivalEcoterm = arrivalDueDate + ' ' + po?.logistic_label;
      }

      const rowData :any[] = [
        [
          {
            text: orderDate,
          },
          {
            text: po.id.toString(),
            bold: true,
          },
        ],
        // po.items_list.map(item => { return { text: item } }),
        // items here
        {
          style: 'table',
          table: {
            widths: widths,
            headerRows: 1,
            body: rowItems,
          },
          layout: 'lightHorizontalLines',
        },
        {
          text: unitsReceived,
          alignment: 'right',
        },
        {
          text: quantity,
          alignment: 'right',
        },
        {
          text: estArrivalEcoterm,
          alignment: 'right',
        },
        {
          text: this.curPipe.transform(receivable_cost),
          alignment: 'right',
        },
        {
          text: this.curPipe.transform(po.total_paid),
          alignment: 'right',
        },
      ];
      if (!this.UserPermission.can('exportUnitCost')) {
        rowData.splice(4, 1);
      }

      tableData.push(rowData);
    }

    // prepare header
    const reportTitle :any[] = [
      {
        text: title,
        alignment: 'right',
        style: 'poheaderright',
      },
    ];
    if (export_type && export_type == 10) {
      reportTitle.push({
        text: 'Order Receivables',
        alignment: 'right',
        fontSize: 15,
        bold: true,
      });
    }
    const headerContent = [
      {
        columns: [
          {
            alignment: 'left',
            image: logoUri,
            width: 250,
            style: 'logo',
          },
          reportTitle,
        ],
      },
    ];

    const footerContent = [];

    const columns :string[] = ['auto', '*', 'auto', 'auto', 'auto', 'auto', 'auto'];
    if (!this.UserPermission.can('exportUnitCost')) {
      columns.splice(4, 1);
    }

    const tableContent :any[] = [
      {
        style: 'table',
        table: {
          widths: columns,
          headerRows: 1,
          body: tableData,
        },
        // layout: "headerLineOnly",
        layout: 'lightHorizontalLines',
      },
    ];

    let pdfContent :any[] = [];

    pdfContent = pdfContent.concat(headerContent, tableContent, footerContent);

    const documentDefinition = {
      pageSize: 'legal',
      pageOrientation: 'landscape',

      content: pdfContent,

      defaultStyle: {
        fontSize: 10,
        columnGap: 20,
      },
      styles: {
        poheaderright: {
          margin: [0, 40, 0, 0],
          fontSize: 18,
          fillColor: '#00ee00',
        },
        logo: {
          fillColor: '#ee0000',
        },
        summarytitle: {
          fontSize: 14,
          bold: true,
        },
        shipping: {
          margin: [0, 25, 0, 0],
        },
        header: {
          fontSize: 16,
          bold: false,
          margin: [0, 15, 0, 5],
          padding: [0, 0, 0, 0],
        },
        subheader: {
          fontSize: 14,
          bold: false,
          margin: [0, 0, 0, 0],
          padding: [0, 0, 0, 0],
        },
        table: {
          margin: [0, 5, 0, 15],
        },
        tableHeader: {
          bold: true,
          fontSize: 13,
          color: 'black',
        },
        warning: {
          color: '#ffb822',
        },
        info: {
          color: '#5578eb',
        },
        danger: {
          color: '#FF0000',
        },
      },
    };
    Swal.close();
    pdfMake.createPdf(documentDefinition).download('PurchaseOrders.pdf');
  }

  // export purchase orders to csv
  exportPurchaseOrderstoCsv(
    purchaseOrders :any[],
    title :string,
    export_type ?:any,
  ) {
    const fileName :string = 'PurchaseOrders';

    // Records sheet
    const jsonData :any = [];

    // purchaseOrders.forEach((po) => {
    // });
    for (let idx = 0; idx < purchaseOrders.length; idx++) {
      const po :any = purchaseOrders[idx];
      let quantity = po.order_quantity;
      const unitsReceived = po.total_received_rfqs
        ? po.total_received_rfqs.quantity
        : 0;

      let receivable_cost = po.total_cost;

      const orderDate :string =
        this.clientOrderDatesService.transformToDatetimeString(
          po.order_date,
          dateConfig.defaultDatePipe,
        );

      // po.items_summary = this.poSvc.getItemsTexts(po);
      // po.items_list = this.poSvc.getItemsTextsToRowsArray(po);
      if (po.total_shipping_cost == 0) {
        po.total_shipping_cost = po.landed_domestic + po.landed_intl;
      }
      let rowData :any = {};
      let estArrivalEcoterm = po?.logistic_label;
      if (po?.arrival_due_date) {
        const arrivalDueDate = this.datePipe.transform(
          po?.arrival_due_date,
          dateConfig.defaultDatePipe,
        );
        estArrivalEcoterm = arrivalDueDate + ' ' + po?.logistic_label;
      }

      if (export_type && export_type == 10) {
        if (po.total_received_rfqs) {
          // check if paid in full
          if (po.total_paid >= po.total_cost) {
            quantity = quantity - po.total_received_rfqs.quantity;
            receivable_cost = po.total_cost - po.total_received_rfqs.line_total;
          } else {
            if (po.total_paid <= po.total_received_rfqs.line_total) {
              continue;
            }
            receivable_cost = po.total_paid - po.total_received_rfqs.line_total;
            const receivableQty :any = quantity * (po.total_paid / po.total_cost);
            const toReceivedQty =
              receivableQty * (po.total_received_rfqs.line_total / po.total_paid);
            quantity = this.curPipe.transform(toReceivedQty, ' ', false, '1.0-0');
          }
        } else {
          receivable_cost = po.total_paid;
          const receivableQty :any = quantity * (po.total_paid / po.total_cost);
          quantity = this.curPipe.transform(receivableQty, ' ', false, '1.0-0'); //receivableQty.toString() + '/' + quantity.toString() + "*";
        }

        // if (po.total_received_rfqs) {
        //     quantity = quantity - po.total_received_rfqs.quantity;
        //     total_cost =
        //         po.total_cost - po.total_received_rfqs.line_total;
        // }

        if (quantity < 0) {
          quantity = 0;
        }
        if (receivable_cost < 0) {
          receivable_cost = 0;
        }

        po.purchase_order_items.forEach((item, idx) => {
          const receivedQty :number = item.received_entries
            ? item.received_entries.quantity
            : 0;
          const receivedDate :any =
            item.received_entries && item.received_entries.receiving_report
              ? this.datePipe.transform(
                  item.received_entries.receiving_report.received_date,
                  dateConfig.defaultDatePipe,
                )
              : '';
          const landedCost :number =
            item.unit_price +
            (((item.unit_price * item.quantity) / po.total_cost) *
              po.total_shipping_cost) /
              item.quantity;
          rowData = {
            'PO#': po.id.toString(),
            'Order Date': orderDate,
            SKU:
              item.internal_sku &&
              item.internal_sku != 'N/A' &&
              item.internal_sku != 'null'
                ? item.internal_sku
                : '',
            'PRODUCT NAME': item.product_name,
            'Qty Ordered': item.quantity.toString(),
            'Qty Received': receivedQty.toString(),
            'Date Received': receivedDate,
            'Unit Cost Landed': this.curPipe.transform(landedCost),
            'Unit Cost Ordered': this.curPipe.transform(item.unit_price),
            'Total Units Received': unitsReceived,
            'Total Receivable Quantity': quantity,
            'Estimated Arrival / Incoterm': estArrivalEcoterm,
            'Receivable Cost': this.curPipe.transform(receivable_cost),
            'Total Paid': this.curPipe.transform(po.total_paid),
          };

          if (!this.UserPermission.can('exportUnitCost')) {
            delete rowData['Unit Cost Landed'];
            delete rowData['Unit Cost Ordered'];
          }
          jsonData.push(rowData);
        });
      } else {
        po.purchase_order_items.forEach((item, idx) => {
          const receivedQty :number = item.received_entries
            ? item.received_entries.quantity
            : 0;
          const landedCost :number =
            item.unit_price +
            (((item.unit_price * item.quantity) / po.total_cost) *
              po.total_shipping_cost) /
              item.quantity;
          rowData = {
            'PO#': po.id.toString(),
            'Order Date': orderDate,
            SKU:
              item.internal_sku &&
              item.internal_sku != 'N/A' &&
              item.internal_sku != 'null'
                ? item.internal_sku
                : '',
            'PRODUCT NAME': item.product_name,
            'Qty Ordered': item.quantity.toString(),
            'Qty Received': receivedQty.toString(),
            'Unit Cost Landed': this.curPipe.transform(landedCost),
            'Unit Cost Ordered': this.curPipe.transform(item.unit_price),
            'Total Units Received': unitsReceived,
            'Total Quantity': quantity,
            'Estimated Arrival / Incoterm': estArrivalEcoterm,
            'Total Cost': this.curPipe.transform(receivable_cost),
            'Total Paid': this.curPipe.transform(po.total_paid),
          };
          if (!this.UserPermission.can('exportUnitCost')) {
            delete rowData['Unit Cost Landed'];
            delete rowData['Unit Cost Ordered'];
            delete rowData['Total Cost'];
          }
          jsonData.push(rowData);
        });
      }
      // jsonData.push(rowData);
    }

    const recordsSheet :XLSX.WorkSheet = XLSX.utils.json_to_sheet(jsonData);
    recordsSheet['A1'].s = { font: { bold: true } };
    recordsSheet['B1'].s = { font: { bold: true } };
    recordsSheet['C1'].s = { font: { bold: true } };
    recordsSheet['D1'].s = { font: { bold: true } };
    // recordsSheet["!cols"] = [
    //  { wch: 25 },
    //  { wch: 25 },
    //  { wch: 25 },
    //  { wch: 25 },
    // ];

    const wb :XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, recordsSheet, 'Records');

    const excelBuffer :any = XLSX.write(wb, {
      bookType: 'csv',
      type: 'array',
    });

    Swal.close();

    this.saveExcelFile(excelBuffer, fileName, '.csv');
  }

  // export commitments report
  exportCommitmentsReportToPDF(purchaseOrders, title) {
    this.getImgDataUrl(
      environment.clientConfig.assetImgDir + 'logo-print.png',
      (imgUri :any) => {
        this.createCommitmentsReportPDF(purchaseOrders, title, imgUri);
      },
    );
  }

  createCommitmentsReportPDF(purchaseOrders, title, logoUri) {
    // prepare table data
    const tableData :any[] = [
      [
        { text: 'Order Date', bold: true },
        {
          text: 'Products [Qty Received/Ordered, Unit Cost Landed/Ordered]',
          bold: true,
        },
        { text: 'Order Quantity', alignment: 'right', bold: true },
        { text: 'Estimated Arrival Date', alignment: 'right', bold: true },
        { text: 'Factory', alignment: 'right', bold: true },
        { text: 'Total Order Cost', alignment: 'right', bold: true },
        { text: 'Payment Terms', alignment: 'center', bold: true },
        { text: 'Total Paid', alignment: 'right', bold: true },
        { text: 'Payment Due', alignment: 'right', bold: true },
        { text: 'Due Date', alignment: 'right', bold: true },
      ],
    ];

    if (!this.UserPermission.can('exportUnitCost')) {
      tableData[0].splice(5, 1);
    }

    const today = new Date();
    purchaseOrders.forEach((po, idx) => {
      // po.items_list = this.poSvc.getItemsTextsToRowsArray(po);

      const orderDate :string =
        this.clientOrderDatesService.transformToDatetimeString(
          po.order_date,
          dateConfig.defaultDatePipe,
        );

      let due_amount :any = { text: '' };
      let due_date :any = { text: '' };

      if (po.total_shipping_cost == 0) {
        po.total_shipping_cost = po.landed_domestic + po.landed_intl;
      }

      if (po.due_amount && po.due_amount > 0) {
        due_amount = {
          text: this.curPipe.transform(po.due_amount),
          alignment: 'right',
          color: today >= new Date(po.due_date) ? 'red' : 'orange',
        };
        due_date = {
          text: this.datePipe.transform(
            new Date(po.due_date),
            dateConfig.defaultDatePipe,
          ),
          color: today >= new Date(po.due_date) ? 'red' : 'skyblue',
        };
      } else {
        // po.payment_terms
        const amount = po.total_cost + po.total_shipping_cost - po.total_paid;
        let date_due = new Date(po.arrival_due_date);

        if (po.payment_terms == 'Net 15') {
          date_due = this.addDays(date_due, 15);
        } else if (po.payment_terms == 'Net 20') {
          date_due = this.addDays(date_due, 20);
        } else if (po.payment_terms == 'Net 30') {
          date_due = this.addDays(date_due, 30);
        }

        due_amount = {
          text: this.curPipe.transform(amount),
          alignment: 'right',
          color: 'orange',
        };
        due_date = {
          text: this.datePipe.transform(date_due, dateConfig.defaultDatePipe),
          color: 'skyblue',
        };
      }

      const rowData = [
        [
          {
            text: orderDate,
          },
          {
            text: po.id.toString(),
            bold: true,
          },
        ],
        this.poSvc.getItemsTexts(po),
        // po.items_list.map(item => { return { text: item } }),
        {
          text: po.order_quantity,
          alignment: 'right',
        },
        {
          text: this.datePipe.transform(
            po.arrival_due_date,
            dateConfig.defaultDatePipe,
          ),
          alignment: 'right',
        },
        {
          text: po.factory.factory_name,
          alignment: 'right',
        },
        {
          text: this.curPipe.transform(po.total_cost + po.total_shipping_cost),
          alignment: 'right',
        },
        {
          text: po.payment_terms,
          alignment: 'center',
        },
        {
          text: this.curPipe.transform(po.total_paid),
          alignment: 'right',
        },
        due_amount,
        due_date,
      ];
      if (!this.UserPermission.can('exportUnitCost')) {
        rowData.splice(5, 1);
      }

      tableData.push(rowData);
    });

    // prepare header
    const reportTitle :any[] = [
      {
        text: title,
        alignment: 'right',
        style: 'poheaderright',
      },
    ];
    const headerContent = [
      {
        columns: [
          {
            alignment: 'left',
            image: logoUri,
            width: 250,
            style: 'logo',
          },
          reportTitle,
        ],
      },
    ];

    const footerContent = [];

    const widths :string[] = [
      'auto',
      '*',
      'auto',
      'auto',
      'auto',
      'auto',
      'auto',
      'auto',
      'auto',
      'auto',
    ];
    if (!this.UserPermission.can('exportUnitCost')) {
      widths.splice(5, 1);
    }
    const tableContent :any[] = [
      {
        style: 'table',
        table: {
          widths: widths,
          headerRows: 1,
          body: tableData,
        },
        // layout: "headerLineOnly",
        layout: 'lightHorizontalLines',
      },
    ];

    let pdfContent :any[] = [];

    pdfContent = pdfContent.concat(headerContent, tableContent, footerContent);

    const documentDefinition = {
      pageSize: 'letter',
      pageOrientation: 'landscape',

      content: pdfContent,

      defaultStyle: {
        fontSize: 10,
        columnGap: 20,
      },
      styles: {
        poheaderright: {
          margin: [0, 40, 0, 0],
          fontSize: 18,
          fillColor: '#00ee00',
        },
        logo: {
          fillColor: '#ee0000',
        },
        summarytitle: {
          fontSize: 14,
          bold: true,
        },
        shipping: {
          margin: [0, 25, 0, 0],
        },
        header: {
          fontSize: 16,
          bold: false,
          margin: [0, 15, 0, 5],
          padding: [0, 0, 0, 0],
        },
        subheader: {
          fontSize: 14,
          bold: false,
          margin: [0, 0, 0, 0],
          padding: [0, 0, 0, 0],
        },
        table: {
          margin: [0, 5, 0, 15],
        },
        tableHeader: {
          bold: true,
          fontSize: 13,
          color: 'black',
        },
        warning: {
          color: '#ffb822',
        },
        info: {
          color: '#5578eb',
        },
        danger: {
          color: '#FF0000',
        },
      },
    };
    Swal.close();
    pdfMake.createPdf(documentDefinition).download('CommitmentsReport.pdf');
  }

  exportCommitmentsReportToCSV(purchaseOrders :any[]) {
    const fileName :string = 'CommitmentsReport';

    // Records sheet
    const jsonData :any = [];

    const today = new Date();
    purchaseOrders.forEach((po, idx) => {
      const orderDate :string =
        this.clientOrderDatesService.transformToDatetimeString(
          po.order_date,
          dateConfig.defaultDatePipe,
        );

      let due_amount :any = { text: '' };
      let due_date :any = { text: '' };

      if (po.total_shipping_cost == 0) {
        po.total_shipping_cost = po.landed_domestic + po.landed_intl;
      }

      if (po.due_amount && po.due_amount > 0) {
        due_amount = this.curPipe.transform(po.due_amount);
        due_date = this.datePipe.transform(
          new Date(po.due_date),
          dateConfig.defaultDatePipe,
        );
      } else {
        // po.payment_terms
        const amount = po.total_cost + po.total_shipping_cost - po.total_paid;
        let date_due = new Date(po.arrival_due_date);

        if (po.payment_terms == 'Net 15') {
          date_due = this.addDays(date_due, 15);
        } else if (po.payment_terms == 'Net 20') {
          date_due = this.addDays(date_due, 20);
        } else if (po.payment_terms == 'Net 30') {
          date_due = this.addDays(date_due, 30);
        }

        due_amount = this.curPipe.transform(amount);
        due_date = this.datePipe.transform(date_due, dateConfig.defaultDatePipe);
      }

      const rowData = {
        'PO#': po.id.toString(),
        'Order Date': orderDate,
        Products: this.poSvc.getItemsTexts(po),
        'Order Quantity': po.order_quantity,
        'Estimated Arrival Date': this.datePipe.transform(
          po.arrival_due_date,
          dateConfig.defaultDatePipe,
        ),
        Factory: po.factory.factory_name,
        'Total Order': this.curPipe.transform(
          po.total_cost + po.total_shipping_cost,
        ),
        'Payment Terms': po.payment_terms,
        'Total Paid': this.curPipe.transform(po.total_paid),
        'Payment Due': due_amount,
        'Due Date': due_date,
      };

      jsonData.push(rowData);
    });

    const recordsSheet :XLSX.WorkSheet = XLSX.utils.json_to_sheet(jsonData);
    recordsSheet['A1'].s = { font: { bold: true } };
    recordsSheet['B1'].s = { font: { bold: true } };
    recordsSheet['C1'].s = { font: { bold: true } };
    recordsSheet['D1'].s = { font: { bold: true } };
    recordsSheet['E1'].s = { font: { bold: true } };
    recordsSheet['F1'].s = { font: { bold: true } };
    recordsSheet['G1'].s = { font: { bold: true } };
    recordsSheet['H1'].s = { font: { bold: true } };
    recordsSheet['I1'].s = { font: { bold: true } };
    recordsSheet['!cols'] = [
      { wch: 35 },
      { wch: 35 },
      { wch: 100 },
      { wch: 35 },
      { wch: 35 },
      { wch: 35 },
      { wch: 35 },
      { wch: 35 },
      { wch: 35 },
    ];

    const wb :XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, recordsSheet, 'Records');

    const excelBuffer :any = XLSX.write(wb, {
      bookType: 'csv',
      type: 'array',
    });

    Swal.close();

    this.saveExcelFile(excelBuffer, fileName, '.csv');
  }

  addDays(date :Date, days :number) :Date {
    date.setDate(date.getDate() + days);

    return date;
  }

  exportSalesReporttoPDF(salesReports :any[], title :string, export_type ?:any) {
    // const logoUri = await this.getImgDataUrl('/assets/img/logo-print.png');
    this.getImgDataUrl(
      environment.clientConfig.assetImgDir + 'logo-print.png',
      (imgUri :any) => {
        this.createSalesReportPDF(salesReports, title, imgUri, export_type);
      },
    );
  }

  createSalesReportPDF(salesReports :any[], title, logoUri, export_type ?:any) {
    // prepare table data
    const tableData :any[] = [
      [
        { text: 'Order Details', bold: true },
        { text: 'Line Item', bold: true },
        { text: 'Order Total', bold: true },
      ],
    ];

    salesReports.forEach((so, idx) => {});
    for (let idx = 0; idx < salesReports.length; idx++) {
      const so :any = salesReports[idx];
      const rowItems :any[] = [
        [
          'SKU',
          'Product Name',
          'Department',
          'Price',
          'Quantity',
          'UOM', // New header for UOM
          'Line Total',
        ],
      ];
      const totals :any[] = [['Total Units', 'OrderTotal']];
      let totalUnits = 0;

      if (!Array.isArray(so.items)) {
        try {
          so.items = JSON.parse(so.items);
        } catch (e) {
          so.items = [];
        }
      }

      if (so.items && so.items.length > 0) {
        so.items.forEach((item) => {
          totalUnits += item.quantity;
          const dept = item.rfq ? item.rfq.product_dept : 'N/A';
          rowItems.push([
            item.sku,
            item.name,
            dept,
            item.unitPrice,
            item.quantity,
            item?.ordered_uom?.name || 'N/A', // Adding UOM column value
            item.unitPrice * item.quantity,
          ]);
        });
      } else {
        rowItems.push(['', '', '', '', '', '', '']);
      }

      totals.push([totalUnits, so.orderTotal]);

      tableData.push([
        [
          {
            text: this.datePipe.transform(so.orderDate, dateConfig.defaultDatePipe),
          },
          {
            text: 'ID: ' + so.orderId.toString().padStart(10, '0'),
            bold: true,
          },
          {
            text: 'Order Number: ' + so.orderNumber.toString().padStart(10, '0'),
            bold: true,
          },
        ],
        {
          style: 'table',
          table: {
            widths: ['auto', '*', 'auto', 'auto', 'auto', 'auto', 'auto'], // Adjusted widths for new column
            headerRows: 1,
            body: rowItems,
          },
          layout: 'lightHorizontalLines',
        },
        {
          style: 'table',
          table: {
            widths: ['auto', 'auto'],
            headerRows: 1,
            body: totals,
          },
          layout: 'lightHorizontalLines',
        },
      ]);
    }

    // prepare header
    const reportTitle :any[] = [
      {
        text: title,
        alignment: 'right',
        style: 'poheaderright',
      },
    ];
    const headerContent = [
      {
        columns: [
          {
            alignment: 'left',
            image: logoUri,
            width: 250,
            style: 'logo',
          },
          reportTitle,
        ],
      },
    ];

    const footerContent = [];

    const tableContent :any[] = [
      {
        style: 'table',
        table: {
          widths: ['auto', '*', 'auto'],
          headerRows: 1,
          body: tableData,
        },
        layout: 'lightHorizontalLines',
      },
    ];

    let pdfContent :any[] = [];

    pdfContent = pdfContent.concat(headerContent, tableContent, footerContent);

    const documentDefinition = {
      pageSize: 'legal',
      pageOrientation: 'landscape',

      content: pdfContent,

      defaultStyle: {
        fontSize: 10,
        columnGap: 20,
      },
      styles: {
        poheaderright: {
          margin: [0, 40, 0, 0],
          fontSize: 18,
          fillColor: '#00ee00',
        },
        logo: {
          fillColor: '#ee0000',
        },
        summarytitle: {
          fontSize: 14,
          bold: true,
        },
        shipping: {
          margin: [0, 25, 0, 0],
        },
        header: {
          fontSize: 16,
          bold: false,
          margin: [0, 15, 0, 5],
          padding: [0, 0, 0, 0],
        },
        subheader: {
          fontSize: 14,
          bold: false,
          margin: [0, 0, 0, 0],
          padding: [0, 0, 0, 0],
        },
        table: {
          margin: [0, 5, 0, 15],
        },
        tableHeader: {
          bold: true,
          fontSize: 13,
          color: 'black',
        },
        warning: {
          color: '#ffb822',
        },
        info: {
          color: '#5578eb',
        },
        danger: {
          color: '#FF0000',
        },
      },
    };
    Swal.close();
    pdfMake.createPdf(documentDefinition).download('sales_report.pdf');
  }

  // export sales report to csv
  exportSalesReportCsv(salesReport :any[], title :string, export_type ?:any) {
    const fileName :string = 'sales_report';

    // Records sheet
    const jsonData :any = [];

    for (let idx = 0; idx < salesReport.length; idx++) {
      const so :any = salesReport[idx];
      let rowData :any = {};
      let totalUnits = 0;

      if (!Array.isArray(so.items)) {
        try {
          so.items = JSON.parse(so.items);
        } catch (e) {
          so.items = [];
        }
      }

      if (so.items && so.items.length > 0) {
        so.items.forEach((item, idx) => {
          totalUnits += item.quantity;
          rowData = {
            ID: so.id,
            'Order Date': this.datePipe.transform(
              so.orderDate,
              dateConfig.defaultDatePipe,
            ),
            'Order ID': so.orderId.toString().padStart(10, '0'),
            'Order Number': so.orderNumber.toString().padStart(10, '0'),
            SKU: item.sku,
            'PRODUCT NAME': item.name,
            DEPARTMENT: item.rfq ? item.rfq.product_dept : 'N/A',
            Price: item.unitPrice,
            Quantity: item.quantity,
            UOM: item.ordered_uom?.name || 'N/A', // Added UOM column
            'Line Total': item.unitPrice * item.quantity,
            'Order Total': so.orderTotal,
          };
          jsonData.push(rowData);
        });
      } else {
        rowData = {
          ID: so.id,
          'Order Date': this.datePipe.transform(
            so.orderDate,
            dateConfig.defaultDatePipe,
          ),
          'Order ID': so.orderId.toString().padStart(10, '0'),
          'Order Number': so.orderNumber.toString().padStart(10, '0'),
          SKU: '',
          'PRODUCT NAME': '',
          DEPARTMENT: '',
          Price: '',
          Quantity: '',
          UOM: '', // Added UOM column
          'Line Total': '',
          'Order Total': so.orderTotal,
        };
        jsonData.push(rowData);
      }
    }

    const recordsSheet :XLSX.WorkSheet = XLSX.utils.json_to_sheet(jsonData);

    // Make headers bold
    Object.keys(recordsSheet).forEach((key) => {
      if (key.startsWith('A1') || key.startsWith('B1') || key.startsWith('C1')) {
        recordsSheet[key].s = { font: { bold: true } };
      }
    });

    const wb :XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, recordsSheet, 'Records');

    const excelBuffer :any = XLSX.write(wb, {
      bookType: 'csv',
      type: 'array',
    });

    Swal.close();

    this.saveExcelFile(excelBuffer, fileName, '.csv');
  }

  exportSalesReportBySKUtoPDF(salesReports :any, title :string, departments :any) {
    // const logoUri = await this.getImgDataUrl('/assets/img/logo-print.png');
    this.getImgDataUrl(
      environment.clientConfig.assetImgDir + 'logo-print.png',
      (imgUri :any) => {
        this.createSalesReportBySKUtoPDF(salesReports, title, imgUri, departments);
      },
    );
  }

  createSalesReportBySKUtoPDF(
    salesReports :any,
    title :string,
    logoUri :any,
    departments :any,
  ) {
    // const depts: any= departments.map((row) => )
    const tableData :any[] = [
      [
        { text: 'SKU', bold: true },
        { text: 'Name', bold: true },
        { text: 'Department', bold: true },
        { text: 'Sales Qty', bold: true, alignment: 'right' },
        { text: 'Sales Amount', bold: true, alignment: 'right' },
      ],
    ];
    const totals :any = { qty: 0, amount: 0 };
    salesReports.forEach((item, idx) => {
      let deptText :string = '';
      if (item.rfq) {
        const dept = departments.find((d :any) => d.id == item.rfq.product_dept_id);
        deptText = dept ? dept.text : '';
      } else if (item.kit) {
        deptText = 'KIT';
      }
      tableData.push([
        {
          text: item.rfq
            ? item.rfq.internal_sku
            : item.kit
              ? item.kit.sku
              : item.sku,
        },
        {
          text: item.rfq
            ? item.rfq.product_name
            : item.kit
              ? item.kit.title
              : item.name,
        },
        { text: deptText },
        { text: item.quantity, alignment: 'right' },
        { text: this.curPipe.transform(item.amount), alignment: 'right' },
      ]);
      totals.qty = totals.qty + parseInt(item.quantity);
      totals.amount = totals.amount + parseFloat(item.amount);
    });

    tableData.push([
      { text: '' },
      { text: 'TOTAL SALES', bold: true },
      { text: '' },
      { text: totals.qty, alignment: 'right' },
      { text: this.curPipe.transform(totals.amount), alignment: 'right' },
    ]);

    // prepare header
    const reportTitle :any[] = [
      {
        text: title,
        alignment: 'right',
        style: 'poheaderright',
      },
    ];
    const headerContent = [
      {
        columns: [
          {
            alignment: 'left',
            image: logoUri,
            width: 250,
            style: 'logo',
          },
          reportTitle,
        ],
      },
    ];

    const footerContent = [];

    const tableContent :any[] = [
      {
        style: 'table',
        table: {
          widths: ['auto', '*', 'auto', 'auto', 'auto'],
          headerRows: 1,
          body: tableData,
        },
        // layout: "headerLineOnly",
        layout: 'lightHorizontalLines',
      },
    ];

    let pdfContent :any[] = [];

    pdfContent = pdfContent.concat(headerContent, tableContent, footerContent);

    const documentDefinition = {
      pageSize: 'legal',
      pageOrientation: 'landscape',

      content: pdfContent,

      defaultStyle: {
        fontSize: 10,
        columnGap: 20,
      },
      styles: {
        poheaderright: {
          margin: [0, 40, 0, 0],
          fontSize: 18,
          fillColor: '#00ee00',
        },
        logo: {
          fillColor: '#ee0000',
        },
      },
    };
    Swal.close();
    pdfMake.createPdf(documentDefinition).download('sales_by_sku_report.pdf');
  }

  createSalesReportBySKUtoCSV(salesReports :any, departments :any) {
    const fileName :string = 'sales_report_by_sku';

    // Records sheet
    const jsonData :any = [];

    const totals :any = { qty: 0, amount: 0 };
    salesReports.forEach((item, idx) => {
      let deptText :string = '';
      if (item.rfq) {
        const dept = departments.find((d :any) => d.id == item.rfq.product_dept_id);
        deptText = dept ? dept.text : '';
      } else if (item.kit) {
        deptText = 'KIT';
      }
      const rowData = {
        SKU: item.rfq ? item.rfq.internal_sku : item.kit ? item.kit.sku : item.sku,
        Name: item.rfq
          ? item.rfq.product_name
          : item.kit
            ? item.kit.title
            : item.name,
        Department: deptText,
        'Sales Qty': item.quantity,
        'Sales Amount': this.curPipe.transform(item.amount),
      };

      jsonData.push(rowData);

      totals.qty = totals.qty + parseInt(item.quantity);
      totals.amount = totals.amount + parseFloat(item.amount);
    });

    jsonData.push({
      SKU: '',
      Name: '',
      Department: '',
      'Sales Qty': '',
      'Sales Amount': '',
    });

    const rowData = {
      SKU: 'TOTAL SALES',
      Name: '',
      Department: '',
      'Sales Qty': totals.qty,
      'Sales Amount': this.curPipe.transform(totals.amount),
    };

    jsonData.push(rowData);

    // jsonData.push(rowData);

    const recordsSheet :XLSX.WorkSheet = XLSX.utils.json_to_sheet(jsonData);
    recordsSheet['A1'].s = { font: { bold: true } };
    recordsSheet['B1'].s = { font: { bold: true } };
    recordsSheet['C1'].s = { font: { bold: true } };
    recordsSheet['D1'].s = { font: { bold: true } };

    const wb :XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, recordsSheet, 'Records');

    const excelBuffer :any = XLSX.write(wb, {
      bookType: 'csv',
      type: 'array',
    });

    Swal.close();

    this.saveExcelFile(excelBuffer, fileName, '.csv');
  }

  exportPOPayments(purchaseOrders :any[]) {
    const fileName :string = 'POPaymentsReport';

    // Records sheet
    const jsonData :any = [];

    const today = new Date();
    purchaseOrders.forEach((po, idx) => {
      po.purchase_order_payments.forEach((o_po, idx) => {
        const payment_type = this.paymentTypes.find(
          (x) => x.id === o_po.payment_type,
        ).text;

        const rowData = {
          'PO#': po.id.toString(),
          Date: this.datePipe.transform(
            o_po.payment_date,
            dateConfig.defaultDatePipe,
          ),
          'Payment Type': payment_type,
          Amount: this.curPipe.transform(o_po.paid_amount),
          Factory: po.factory.factory_name,
        };

        jsonData.push(rowData);
      });
    });

    const recordsSheet :XLSX.WorkSheet = XLSX.utils.json_to_sheet(jsonData);
    recordsSheet['A1'].s = { font: { bold: true } };
    recordsSheet['B1'].s = { font: { bold: true } };
    recordsSheet['C1'].s = { font: { bold: true } };
    recordsSheet['D1'].s = { font: { bold: true } };
    recordsSheet['E1'].s = { font: { bold: true } };
    recordsSheet['!cols'] = [
      { wch: 50 },
      { wch: 35 },
      { wch: 35 },
      { wch: 50 },
      { wch: 35 },
    ];

    const wb :XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, recordsSheet, 'Records');

    const excelBuffer :any = XLSX.write(wb, {
      bookType: 'csv',
      type: 'array',
    });

    Swal.close();

    this.saveExcelFile(excelBuffer, fileName, '.csv');
  }

  exportShippingInvoicePayments(purchaseOrders :any[]) {
    const fileName :string = 'ShippingInvoicePaymentsReport';

    // Records sheet
    const jsonData :any = [];

    const today = new Date();
    purchaseOrders.forEach((po, idx) => {
      po.purchase_order_shipping_costs.forEach((osc, idx) => {
        const rowData = {
          'PO#': po.id.toString(),
          Date: this.datePipe.transform(
            osc.shipping_date,
            dateConfig.defaultDatePipe,
          ),
          Amount: this.curPipe.transform(osc.cost_amount),
          'Added to PO cost': osc.include_in_total == 1 ? 'YES' : 'NO',
          Factory: po.factory.factory_name,
        };

        jsonData.push(rowData);
      });
    });

    const recordsSheet :XLSX.WorkSheet = XLSX.utils.json_to_sheet(jsonData);
    recordsSheet['A1'].s = { font: { bold: true } };
    recordsSheet['B1'].s = { font: { bold: true } };
    recordsSheet['C1'].s = { font: { bold: true } };
    recordsSheet['D1'].s = { font: { bold: true } };
    recordsSheet['E1'].s = { font: { bold: true } };
    recordsSheet['!cols'] = [
      { wch: 50 },
      { wch: 35 },
      { wch: 35 },
      { wch: 50 },
      { wch: 35 },
    ];

    const wb :XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, recordsSheet, 'Records');

    const excelBuffer :any = XLSX.write(wb, {
      bookType: 'csv',
      type: 'array',
    });

    Swal.close();

    this.saveExcelFile(excelBuffer, fileName, '.csv');
  }

  exportClientQuotationtoPDF(quotation :any) :void {
    const logo = environment.clientConfig.assetImgDir + 'logo-print.png';
    let temp_logo = null;

    try {
      temp_logo = quotation.my_profile_settings.logo.base64;
    } catch (e) {}

    if (temp_logo != null) {
      this.createClientQuotationPDF(quotation, temp_logo);
    } else {
      this.getImgDataUrl(logo, (imgUri :any) => {
        this.createClientQuotationPDF(quotation, imgUri);
      });
    }
  }

  exportClientPOtoPackagingslipPDF(purchaseOrder :any) {
    const logo = environment.clientConfig.assetImgDir + 'logo-print.png';
    let temp_logo = null;
    try {
      temp_logo = purchaseOrder.my_profile_settings.logo;
    } catch (e) {}
    if (temp_logo && temp_logo != 'data:image/png;base64,') {
      this.createPackagingSlipPDF(purchaseOrder, temp_logo);
    } else {
      this.getImgDataUrl(logo, (imgUri :any) => {
        this.createPackagingSlipPDF(purchaseOrder, imgUri);
      });
    }
  }

  exportClientInvoicestoPDF(purchaseOrder :any) :void {
    const logo = environment.clientConfig.assetImgDir + 'logo-print.png';
    let temp_logo = null;
    try {
      temp_logo = purchaseOrder.my_profile_settings.logo.base64;
    } catch (e) {}

    if (temp_logo != null) {
      this.createClientInvoicesPDF(purchaseOrder, temp_logo);
    } else {
      this.getImgDataUrl(logo, (imgUri :any) => {
        this.createClientInvoicesPDF(purchaseOrder, imgUri);
      });
    }
  }

  exportClientPaymentstoCSV(purchaseOrder :any) {
    const fileName :string = 'ClientPayments';

    // Records sheet
    const jsonData :any = [];

    const today = new Date();
    purchaseOrder.payments.forEach((payment, idx) => {
      const rowData = {
        'PO#': '#' + purchaseOrder.id.toString(),
        Date: this.datePipe.transform(
          payment.paymentDate,
          dateConfig.defaultDatePipe,
        ),
        Type: payment.paymentType,
        Amount: this.curPipe.transform(payment.paymentAmount),
      };

      jsonData.push(rowData);
    });

    const recordsSheet :XLSX.WorkSheet = XLSX.utils.json_to_sheet(jsonData);

    const wb :XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, recordsSheet, 'Records');

    const excelBuffer :any = XLSX.write(wb, {
      bookType: 'csv',
      type: 'array',
    });

    Swal.close();

    this.saveExcelFile(excelBuffer, fileName, '.csv');
  }

  exportClientInvoicestoExcel(purchaseOrder :any) {
    const fileName :string = 'ClientInvoices';

    // Records sheet
    const jsonData :any = [];

    const today = new Date();
    purchaseOrder.invoices.forEach((inv, idx) => {
      const rowData = {
        'PO#': '#' + purchaseOrder.id.toString(),
        Customer: inv.customer.businessName,
        Supplier: inv.supplier.businessName,
        'Created Date': this.datePipe.transform(
          inv.created_at,
          dateConfig.defaultDatePipe,
        ),
        'Date Sent': this.datePipe.transform(
          inv.date_sent,
          dateConfig.defaultDatePipe,
        ),
        'Due Date': this.datePipe.transform(
          inv.due_date,
          dateConfig.defaultDatePipe,
        ),
        'Amount Due': this.curPipe.transform(inv.due_amount),
      };

      jsonData.push(rowData);
    });

    const recordsSheet :XLSX.WorkSheet = XLSX.utils.json_to_sheet(jsonData);

    const wb :XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, recordsSheet, 'Records');

    const excelBuffer :any = XLSX.write(wb, {
      bookType: 'xlsx',
      type: 'array',
    });

    Swal.close();

    this.saveExcelFile(excelBuffer, fileName, '.xlsx');
  }

  createClientPackagingslipPDF(purchaseOrder, logoUri) :void {
    const orderStatus =
      purchaseOrder.order_status === ClientPurchaseOrderStatusTextConstant.ESTIMATE
        ? 'Estimate'
        : 'Invoice';

    const tableData :any[] = [
      [
        { text: 'PO#', bold: true },
        { text: 'Customer', bold: true },
        { text: 'Supplier', bold: true },
        { text: 'Created Date', bold: true },
        { text: 'Date Sent', bold: true },
        { text: 'Due Date', bold: true },
        { text: 'Amount Due', alignment: 'right', bold: true },
      ],
    ];

    purchaseOrder.invoices.forEach((inv, idx) => {
      tableData.push([
        purchaseOrder.id.toString(),
        inv.customer.businessName,
        inv.supplier.businessName,
        this.datePipe.transform(inv.created_at, dateConfig.defaultDatePipe),
        this.datePipe.transform(inv.date_sent, dateConfig.defaultDatePipe),
        this.datePipe.transform(inv.due_date, dateConfig.defaultDatePipe),
        {
          text: this.curPipe.transform(inv.due_amount),
          alignment: 'right',
        },
      ]);
    });

    const tableDataLineItems :any[] = [
      [
        { text: 'Item(s)', bold: true },
        { text: 'Description', bold: true },
        { text: 'Unit Price', alignment: 'right', bold: true },
        { text: 'QTY', alignment: 'right', bold: true },
        { text: 'Total', alignment: 'right', bold: true },
      ],
    ];

    purchaseOrder.line_items.forEach((item, idx) => {
      tableDataLineItems.push([
        item.sku,
        item.product_name,
        {
          text: this.curPipe.transform(item.unit_price),
          alignment: 'right',
        },
        { text: item.quantity, alignment: 'right' },
        {
          text: this.curPipe.transform(item.net_total_amount),
          alignment: 'right',
        },
      ]);
    });

    const poNumber :string = '#' + purchaseOrder.id.toString();

    let shipTo = null;

    if (purchaseOrder.client != null) {
      shipTo = [
        purchaseOrder.client.shipping_address,
        purchaseOrder.client.shipping_city,
        purchaseOrder.client.shipping_state,
        purchaseOrder.client.shipping_zip,
        purchaseOrder.client.shipping_country,
      ]
        .filter(Boolean)
        .map((elem) => elem.replace(/\s+/g, ' ').trim())
        .join(', ');
    }

    let billToName = null;

    if (purchaseOrder.client.default_billable_user != null) {
      billToName = [
        purchaseOrder.client.default_billable_user.firstname,
        purchaseOrder.client.default_billable_user.lastname,
      ]
        .filter(Boolean)
        .map((elem) => elem.replace(/\s+/g, ' ').trim())
        .join(' ');
    } else {
      billToName = purchaseOrder.client.business_name;
    }

    const orderDate :string =
      this.clientOrderDatesService.transformToDatetimeString(
        purchaseOrder.order_date,
        dateConfig.defaultDatePipe,
      );

    // prepare header
    const headerContent = [
      {
        columns: [
          [
            {
              text: purchaseOrder?.my_profile_settings?.company_name,
              alignment: 'left',
            },
            {
              text: purchaseOrder?.my_profile_settings?.ship_from_invoice_address,
              alignment: 'left',
            },
            {
              text: purchaseOrder?.my_profile_settings?.email_accounting,
              alignment: 'left',
            },
            {
              text: purchaseOrder?.my_profile_settings?.website,
              alignment: 'left',
            },
          ],
          { text: 'Invoices', alignment: '', style: 'header' },
          {
            alignment: 'right',
            image: logoUri,
            width: 100,
            style: 'logo',
          },
        ],
      },
      {
        columns: [{ text: '', alignment: '', style: 'header' }],
      },
      {
        columns: [
          [
            { text: 'Bill To', alignment: '', style: 'subheader' },
            { text: '', alignment: '', style: 'subheader' },
            {
              text: billToName,
              alignment: 'left',
            },
            {
              text: purchaseOrder?.client?.businessFullAddress,
              alignment: 'left',
            },
          ],

          [
            { text: 'Ship To', alignment: '', style: 'subheader' },
            { text: '', alignment: '', style: 'subheader' },
            { text: shipTo != null ? shipTo : '', alignment: 'left' },
          ],
          [
            { text: 'Details', alignment: '', style: 'subheader' },
            { text: '', alignment: '', style: 'subheader' },
            { text: orderStatus + ' #: ' + purchaseOrder.id, alignment: 'left' },
            {
              text: 'Date: ' + orderDate,
              alignment: 'left',
            },
            {
              text: 'Payment Term: ' + purchaseOrder.payment_term_title,
              alignment: 'left',
            },
            // Still no data for due date
            // { text: 'Due Date: 2/25/2022', alignment: 'left' },
          ],
        ],
      },
    ];

    let total_amount_due :number = 0;

    total_amount_due = purchaseOrder.invoices.reduce(function (prev, cur) {
      return +prev + +cur.due_amount;
    }, 0);

    const totalRows :any = [];
    totalRows.push({
      columns: [
        { text: 'Total Amount Due', alignment: 'right' },
        {
          text: this.curPipe.transform(total_amount_due),
          alignment: 'right',
          style: 'danger',
          bold: true,
          width: 120,
        },
      ],
    });

    const totalRowsLineItem :any = [];
    totalRowsLineItem.splice(0, 0, {
      columns: [
        { text: 'Sub Total', alignment: 'right' },
        {
          text: this.curPipe.transform(purchaseOrder.total_amount),
          alignment: 'right',
          width: 120,
        },
      ],
    });
    totalRowsLineItem.push({
      columns: [
        { text: 'Net Total', alignment: 'right' },
        {
          text: this.curPipe.transform(purchaseOrder.net_total_amount),
          alignment: 'right',
          style: 'warning',
          bold: true,
          width: 120,
        },
      ],
    });

    const footerContent = [
      {
        columns: [totalRows],
      },
    ];

    const footerContentLineItem = [
      {
        columns: [
          [
            {
              columns: [
                {
                  text: purchaseOrder?.b2b_portal_settings?.po_display_message,
                  alignment: 'left',
                },
              ],
            },
            {
              columns: [{ text: '', alignment: '', style: 'header' }],
            },
            {
              columns: [{ text: purchaseOrder.public_note, alignment: 'left' }],
            },
            {
              columns: [{ text: '', alignment: '', style: 'header' }],
            },
          ],
          totalRowsLineItem,
        ],
      },
      {
        columns: [
          {
            text: 'Payment Instructions: ',
            alignment: 'left',
          },
        ],
      },
      {
        columns: [
          {
            text: purchaseOrder?.b2b_portal_settings?.payment_instructions,
            alignment: 'left',
          },
        ],
      },
      { text: '', alignment: '', style: 'header' },
      {
        columns: [
          {
            text: 'Receiving Bank Name: ',
            alignment: 'left',
          },
        ],
      },
      {
        columns: [
          {
            text: purchaseOrder?.client?.default_billable_user?.receiving_bank_name,
            alignment: 'left',
          },
        ],
      },
      { text: '', alignment: '', style: 'header' },
      {
        columns: [
          {
            text: 'Receiving Bank Address: ',
            alignment: 'left',
          },
        ],
      },
      {
        columns: [
          {
            text: purchaseOrder?.client?.default_billable_user
              ?.receiving_bank_address,
            alignment: 'left',
          },
        ],
      },
      {
        columns: [{ text: '', alignment: '', style: 'header' }],
      },
      {
        columns: [
          {
            text: purchaseOrder?.b2b_portal_settings?.company_footer,
            alignment: 'center',
          },
        ],
      },
    ];

    const tableContent :any[] = [
      { text: '', alignment: 'center', style: 'header' },
      {
        style: 'table',
        table: {
          widths: ['auto', '*', '*', 'auto', 'auto', 'auto', 'auto'],
          headerRows: 1,
          body: tableData,
        },
        layout: 'headerLineOnly',
      },
    ];

    const tableContentLineItem :any[] = [
      {
        style: 'table',
        table: {
          widths: ['auto', '*', 'auto', 'auto', 'auto'],
          headerRows: 1,
          body: tableDataLineItems,
        },
        layout: 'headerLineOnly',
      },
    ];

    let pdfContent :any[] = [];

    pdfContent = pdfContent.concat(
      headerContent,
      tableContent,
      footerContent,
      tableContentLineItem,
      footerContentLineItem,
    );

    const documentDefinition = {
      pageSize: 'letter',
      pageOrientation: 'portrait',

      content: pdfContent,

      defaultStyle: {
        fontSize: 10,
        columnGap: 20,
      },
      styles: {
        poheaderright: {
          margin: [0, 25, 0, 0],
          fontSize: 18,
          fillColor: '#00ee00',
        },
        logo: {
          fillColor: '#ee0000',
        },
        summarytitle: {
          fontSize: 14,
          bold: true,
        },
        shipping: {
          margin: [0, 25, 0, 0],
        },
        header: {
          fontSize: 16,
          bold: false,
          margin: [0, 15, 0, 5],
          padding: [0, 0, 0, 0],
        },
        subheader: {
          fontSize: 14,
          bold: false,
          margin: [0, 0, 0, 0],
          padding: [0, 0, 0, 0],
        },
        table: {
          margin: [0, 5, 0, 15],
        },
        tableHeader: {
          bold: true,
          fontSize: 13,
          color: 'black',
        },
        warning: {
          color: '#ffb822',
        },
        info: {
          color: '#5578eb',
        },
        danger: {
          color: '#FF0000',
        },
      },
    };
    Swal.close();
    pdfMake.createPdf(documentDefinition).download('ClientInvoices' + '.pdf');
  }

  createClientInvoicesPDF(purchaseOrder, logoUri) :void {
    const orderStatus =
      purchaseOrder.order_status === ClientPurchaseOrderStatusTextConstant.ESTIMATE
        ? 'Estimate'
        : 'Invoice';

    const tableData :any[] = [
      [
        { text: 'PO#', bold: true },
        { text: 'Customer', bold: true },
        { text: 'Supplier', bold: true },
        { text: 'Created Date', bold: true },
        { text: 'Date Sent', bold: true },
        { text: 'Due Date', bold: true },
        { text: 'Amount Due', alignment: 'right', bold: true },
      ],
    ];

    purchaseOrder.invoices.forEach((inv, idx) => {
      tableData.push([
        purchaseOrder.id.toString(),
        inv.customer.businessName,
        inv.supplier.businessName,
        this.datePipe.transform(inv.created_at, dateConfig.defaultDatePipe),
        this.datePipe.transform(inv.date_sent, dateConfig.defaultDatePipe),
        this.datePipe.transform(inv.due_date, dateConfig.defaultDatePipe),
        {
          text: this.curPipe.transform(inv.due_amount),
          alignment: 'right',
        },
      ]);
    });

    const tableDataLineItems :any[] = [
      [
        { text: 'Item(s)', bold: true },
        { text: 'Description', bold: true },
        { text: 'Unit Price', alignment: 'right', bold: true },
        { text: 'QTY', alignment: 'right', bold: true },
        { text: 'Total', alignment: 'right', bold: true },
      ],
    ];

    purchaseOrder.line_items.forEach((item, idx) => {
      tableDataLineItems.push([
        item.sku,
        item.product_name,
        {
          text: this.curPipe.transform(item.unit_price),
          alignment: 'right',
        },
        { text: item.quantity, alignment: 'right' },
        {
          text: this.curPipe.transform(item.net_total_amount),
          alignment: 'right',
        },
      ]);
    });

    const poNumber :string = '#' + purchaseOrder.id.toString();

    let shipTo = null;

    if (purchaseOrder.client != null) {
      shipTo = [
        purchaseOrder.client.shipping_address,
        purchaseOrder.client.shipping_city,
        purchaseOrder.client.shipping_state,
        purchaseOrder.client.shipping_zip,
        purchaseOrder.client.shipping_country,
      ]
        .filter(Boolean)
        .map((elem) => elem.replace(/\s+/g, ' ').trim())
        .join(', ');
    }

    let billToName = null;

    if (purchaseOrder.client.default_billable_user != null) {
      billToName = [
        purchaseOrder.client.default_billable_user.firstname,
        purchaseOrder.client.default_billable_user.lastname,
      ]
        .filter(Boolean)
        .map((elem) => elem.replace(/\s+/g, ' ').trim())
        .join(' ');
    } else {
      billToName = purchaseOrder.client.business_name;
    }

    const orderDate :string =
      this.clientOrderDatesService.transformToDatetimeString(
        purchaseOrder.order_date,
        dateConfig.defaultDatePipe,
      );

    // prepare header
    const headerContent = [
      {
        columns: [
          [
            {
              text: purchaseOrder?.my_profile_settings?.company_name,
              alignment: 'left',
            },
            {
              text: purchaseOrder?.my_profile_settings?.ship_from_invoice_address,
              alignment: 'left',
            },
            {
              text: purchaseOrder?.my_profile_settings?.email_accounting,
              alignment: 'left',
            },
            {
              text: purchaseOrder?.my_profile_settings?.website,
              alignment: 'left',
            },
          ],
          { text: 'Invoices', alignment: '', style: 'header' },
          {
            alignment: 'right',
            image: logoUri,
            width: 100,
            style: 'logo',
          },
        ],
      },
      {
        columns: [{ text: '', alignment: '', style: 'header' }],
      },
      {
        columns: [
          [
            { text: 'Bill To', alignment: '', style: 'subheader' },
            { text: '', alignment: '', style: 'subheader' },
            {
              text: billToName,
              alignment: 'left',
            },
            {
              text: purchaseOrder?.client?.businessFullAddress,
              alignment: 'left',
            },
          ],

          [
            { text: 'Ship To', alignment: '', style: 'subheader' },
            { text: '', alignment: '', style: 'subheader' },
            { text: shipTo != null ? shipTo : '', alignment: 'left' },
          ],
          [
            { text: 'Details', alignment: '', style: 'subheader' },
            { text: '', alignment: '', style: 'subheader' },
            { text: orderStatus + ' #: ' + purchaseOrder.id, alignment: 'left' },
            {
              text: 'Date: ' + orderDate,
              alignment: 'left',
            },
            {
              text: 'Payment Term: ' + purchaseOrder.payment_term_title,
              alignment: 'left',
            },
            // Still no data for due date
            // { text: 'Due Date: 2/25/2022', alignment: 'left' },
          ],
        ],
      },
    ];

    let total_amount_due :number = 0;

    total_amount_due = purchaseOrder.invoices.reduce(function (prev, cur) {
      return +prev + +cur.due_amount;
    }, 0);

    const totalRows :any = [];
    totalRows.push({
      columns: [
        { text: 'Total Amount Due', alignment: 'right' },
        {
          text: this.curPipe.transform(total_amount_due),
          alignment: 'right',
          style: 'danger',
          bold: true,
          width: 120,
        },
      ],
    });

    const totalRowsLineItem :any = [];
    totalRowsLineItem.splice(0, 0, {
      columns: [
        { text: 'Sub Total', alignment: 'right' },
        {
          text: this.curPipe.transform(purchaseOrder.total_amount),
          alignment: 'right',
          width: 120,
        },
      ],
    });
    totalRowsLineItem.push({
      columns: [
        { text: 'Net Total', alignment: 'right' },
        {
          text: this.curPipe.transform(purchaseOrder.net_total_amount),
          alignment: 'right',
          style: 'warning',
          bold: true,
          width: 120,
        },
      ],
    });

    const footerContent = [
      {
        columns: [totalRows],
      },
    ];

    const footerContentLineItem = [
      {
        columns: [
          [
            {
              columns: [
                {
                  text: purchaseOrder?.b2b_portal_settings?.po_display_message,
                  alignment: 'left',
                },
              ],
            },
            {
              columns: [{ text: '', alignment: '', style: 'header' }],
            },
            {
              columns: [{ text: purchaseOrder.public_note, alignment: 'left' }],
            },
            {
              columns: [{ text: '', alignment: '', style: 'header' }],
            },
          ],
          totalRowsLineItem,
        ],
      },
      {
        columns: [
          {
            text: 'Payment Instructions: ',
            alignment: 'left',
          },
        ],
      },
      {
        columns: [
          {
            text: purchaseOrder?.b2b_portal_settings?.payment_instructions,
            alignment: 'left',
          },
        ],
      },
      { text: '', alignment: '', style: 'header' },
      {
        columns: [
          {
            text: 'Receiving Bank Name: ',
            alignment: 'left',
          },
        ],
      },
      {
        columns: [
          {
            text: purchaseOrder?.client?.default_billable_user?.receiving_bank_name,
            alignment: 'left',
          },
        ],
      },
      { text: '', alignment: '', style: 'header' },
      {
        columns: [
          {
            text: 'Receiving Bank Address: ',
            alignment: 'left',
          },
        ],
      },
      {
        columns: [
          {
            text: purchaseOrder?.client?.default_billable_user
              ?.receiving_bank_address,
            alignment: 'left',
          },
        ],
      },
      {
        columns: [{ text: '', alignment: '', style: 'header' }],
      },
      {
        columns: [
          {
            text: purchaseOrder?.b2b_portal_settings?.company_footer,
            alignment: 'center',
          },
        ],
      },
    ];

    const tableContent :any[] = [
      { text: '', alignment: 'center', style: 'header' },
      {
        style: 'table',
        table: {
          widths: ['auto', '*', '*', 'auto', 'auto', 'auto', 'auto'],
          headerRows: 1,
          body: tableData,
        },
        layout: 'headerLineOnly',
      },
    ];

    const tableContentLineItem :any[] = [
      {
        style: 'table',
        table: {
          widths: ['auto', '*', 'auto', 'auto', 'auto'],
          headerRows: 1,
          body: tableDataLineItems,
        },
        layout: 'headerLineOnly',
      },
    ];

    let pdfContent :any[] = [];

    pdfContent = pdfContent.concat(
      headerContent,
      tableContent,
      footerContent,
      tableContentLineItem,
      footerContentLineItem,
    );

    const documentDefinition = {
      pageSize: 'letter',
      pageOrientation: 'portrait',

      content: pdfContent,

      defaultStyle: {
        fontSize: 10,
        columnGap: 20,
      },
      styles: {
        poheaderright: {
          margin: [0, 25, 0, 0],
          fontSize: 18,
          fillColor: '#00ee00',
        },
        logo: {
          fillColor: '#ee0000',
        },
        summarytitle: {
          fontSize: 14,
          bold: true,
        },
        shipping: {
          margin: [0, 25, 0, 0],
        },
        header: {
          fontSize: 16,
          bold: false,
          margin: [0, 15, 0, 5],
          padding: [0, 0, 0, 0],
        },
        subheader: {
          fontSize: 14,
          bold: false,
          margin: [0, 0, 0, 0],
          padding: [0, 0, 0, 0],
        },
        table: {
          margin: [0, 5, 0, 15],
        },
        tableHeader: {
          bold: true,
          fontSize: 13,
          color: 'black',
        },
        warning: {
          color: '#ffb822',
        },
        info: {
          color: '#5578eb',
        },
        danger: {
          color: '#FF0000',
        },
      },
    };
    Swal.close();
    pdfMake.createPdf(documentDefinition).download('ClientInvoices' + '.pdf');
  }

  createPackagingSlipPDF(purchaseOrder, logoUri) :void {
    const label = this.translate.instant('LABELS.PACKAGING_SKIP');
    const orderStatus =
      purchaseOrder.order_status === ClientPurchaseOrderStatusTextConstant.ESTIMATE
        ? 'Estimate'
        : 'Invoice';

    const lineItemsTableData :any[] = [
      [
        { text: 'Line', bold: true },
        { text: 'SKUs', bold: true },
        { text: 'Products', bold: true },
        { text: 'Description', bold: true },
        { text: 'QTY', alignment: 'right', bold: true },
        { text: 'Unit', alignment: 'right', bold: true },
      ],
    ];
    let totalQty :number = 0;
    purchaseOrder.line_items.forEach((item, idx) => {
      totalQty += item.quantity;
      if (item.item && item.item.id) {
        let uomString :string = '';
        if (item.ordered_uom_id && item.item && item.item.unit_of_measures) {
          const uom :UnitMeasureInterface = item.item.unit_of_measures.find(
            (uom :UnitMeasureInterface) => uom.id == item.ordered_uom_id,
          );
          if (uom) {
            uomString = uom.name;
          }
        }
        const upc :string = item?.item?.upc ? item?.item?.upc : '';
        lineItemsTableData.push([
          idx + 1,
          item.sku ? item.sku : '',
          item.product_name + '\n' + upc,
          item.description,
          { text: item.quantity, alignment: 'right' },
          { text: uomString },
        ]);
      }
    });

    const poNumber :string = '#' + purchaseOrder.id.toString();

    let shipTo = null;

    if (purchaseOrder.client != null) {
      shipTo = [
        purchaseOrder.ship_address,
        purchaseOrder.ship_address_line_2,
        purchaseOrder.ship_city,
        purchaseOrder.ship_state,
        purchaseOrder.ship_zip,
        purchaseOrder.ship_country,
      ]
        .filter(Boolean)
        .map((elem) => elem.replace(/\s+/g, ' ').trim())
        .join(', ');
    }

    let billToName = null;

    if (purchaseOrder.client.default_billable_user != null) {
      billToName = [
        purchaseOrder.client.default_billable_user.firstname,
        purchaseOrder.client.default_billable_user.lastname,
      ]
        .filter(Boolean)
        .map((elem) => elem.replace(/\s+/g, ' ').trim())
        .join(' ');
    } else {
      billToName = purchaseOrder.client.business_name;
    }

    const orderDate :string =
      this.clientOrderDatesService.transformToDatetimeString(
        purchaseOrder.order_date,
        dateConfig.defaultDatePipe,
      );

    // prepare header
    const headerContent = [
      {
        columns: [
          [
            {
              text: purchaseOrder?.my_profile_settings?.company_name,
              alignment: 'left',
            },
            {
              text: purchaseOrder?.my_profile_settings?.ship_from_invoice_address,
              alignment: 'left',
            },
            {
              text: purchaseOrder?.my_profile_settings?.email_accounting,
              alignment: 'left',
            },
            {
              text: purchaseOrder?.my_profile_settings?.website,
              alignment: 'left',
            },
          ],
          [
            {
              text: label,
              alignment: 'left',
              style: 'header',
            },
            [
              [
                {
                  text: `${orderStatus} #: ${purchaseOrder.id}`,
                  alignment: 'left',
                },
              ],
              ...(purchaseOrder.client.default_billable_user
                ? [
                    {
                      text: `Attn: ${purchaseOrder.client.default_billable_user.firstname} ${purchaseOrder.client.default_billable_user.lastname}`,
                      alignment: 'left',
                    },
                  ]
                : []),
            ],
          ],
          {
            alignment: 'right',
            image: logoUri,
            width: 100,
            style: 'logo',
          },
        ],
      },
      {
        columns: [{ text: '', alignment: '', style: 'header' }],
      },
      {
        columns: [
          [
            { text: 'Bill To', alignment: '', style: 'subheader' },
            { text: '', alignment: '', style: 'subheader' },
            {
              text: billToName,
              alignment: 'left',
            },
            {
              text: purchaseOrder?.client?.businessFullAddress,
              alignment: 'left',
            },
            {
              text: purchaseOrder?.client?.default_billable_user?.email,
              alignment: 'left',
            },
          ],

          [
            { text: 'Ship To', alignment: '', style: 'subheader' },
            { text: '', alignment: '', style: 'subheader' },
            {
              text: purchaseOrder.shipping_name,
              alignment: 'left',
            },
            { text: shipTo != null ? shipTo : '', alignment: 'left' },
            {
              text: purchaseOrder?.shipping_phone,
              alignment: 'left',
            },
            {
              text: purchaseOrder?.shipping_email,
              alignment: 'left',
            },
          ],
          [
            { text: 'Details', alignment: '', style: 'subheader' },
            { text: '', alignment: '', style: 'subheader' },
            { text: label + ' #: ' + purchaseOrder.id, alignment: 'left' },
            {
              text: 'Date: ' + orderDate,
              alignment: 'left',
            },
            {
              text: 'Payment Term: ' + purchaseOrder.payment_term_title,
              alignment: 'left',
            },
          ],
        ],
      },
    ];

    const totalRows :any = [];

    const tableContent :any[] = [
      { text: '', alignment: 'center', style: 'header' },
      {
        style: 'table',
        table: {
          widths: ['auto', 'auto', 'auto', '*', 'auto', 'auto'],
          headerRows: 1,
          body: lineItemsTableData,
        },
        layout: 'headerLineOnly',
      },
    ];
    let pdfContent :any[] = [];

    totalRows.push({
      columns: [
        { text: 'Total Quantity', alignment: 'right' },
        {
          text: totalQty,
          alignment: 'right',
          bold: true,
          width: 120,
        },
      ],
    });

    const footerContent = [
      {
        columns: [totalRows],
      },
    ];

    pdfContent = pdfContent.concat(headerContent, tableContent, footerContent);

    const documentDefinition = {
      pageSize: 'letter',
      pageOrientation: 'portrait',

      content: pdfContent,

      defaultStyle: {
        fontSize: 10,
        columnGap: 20,
      },
      styles: {
        poheaderright: {
          margin: [0, 25, 0, 0],
          fontSize: 18,
          fillColor: '#00ee00',
        },
        logo: {
          fillColor: '#ee0000',
        },
        summarytitle: {
          fontSize: 14,
          bold: true,
        },
        shipping: {
          margin: [0, 25, 0, 0],
        },
        header: {
          fontSize: 16,
          bold: false,
          margin: [0, 15, 0, 5],
          padding: [0, 0, 0, 0],
        },
        subheader: {
          fontSize: 14,
          bold: false,
          margin: [0, 0, 0, 0],
          padding: [0, 0, 0, 0],
        },
        table: {
          margin: [0, 5, 0, 15],
        },
        tableHeader: {
          bold: true,
          fontSize: 13,
          color: 'black',
        },
        warning: {
          color: '#ffb822',
        },
        info: {
          color: '#5578eb',
        },
        danger: {
          color: '#FF0000',
        },
      },
    };

    const filename = label + '_' + poNumber + '.pdf';

    Swal.close();
    pdfMake.createPdf(documentDefinition).download(filename);
  }

  createClientQuotationPDF(quotation, logoUri) :void {
    const lineItemsTableData :any[] = [
      [
        { text: 'Line', bold: true },
        { text: 'Item(s)', bold: true },
        { text: 'Product Name', bold: true },
        { text: 'Description', bold: true },
        { text: 'QTY', alignment: 'right', bold: true },
        { text: 'Unit Price', alignment: 'right', bold: true },
        { text: 'Total', alignment: 'right', bold: true },
      ],
    ];

    const otherLineItemsTableData :any[] = [
      [
        { text: 'Other Item(s)', bold: true },
        { text: 'Product Name', bold: true },
        { text: 'Description', bold: true },
        { text: 'QTY', alignment: 'right', bold: true },
        { text: 'Unit Price', alignment: 'right', bold: true },
        { text: 'Total', alignment: 'right', bold: true },
      ],
    ];

    const tieredPricingLineItemsData :any[] = [
      [
        { text: 'Line', bold: true },
        { text: 'Tiered Pricing Item(s)', bold: true },
        { text: 'Product Name', bold: true },
        { text: 'Description', bold: true },
        { text: 'QTY FROM', alignment: 'right', bold: true },
        { text: 'QTY TO', alignment: 'right', bold: true },
        { text: 'Unit Price', alignment: 'right', bold: true },
      ],
    ];

    let totalOverall :number = 0;

    quotation.items.forEach((item, idx, array) => {
      const sku =
        item.item_type === 'custom' ? item.product_name : item[item.item_type].sku;
      const productName =
        item.item_type === 'custom' ? null : item[item.item_type].product_name;
      const estimateDescription =
        item.item_type === 'custom' ? null : item.description;

      totalOverall += item.net_total_amount;

      if (item.tiered_pricings.length) {
        item.tiered_pricings.forEach((tieredPricing, index) => {
          const isFirstItem = !index; //
          tieredPricingLineItemsData.push([
            isFirstItem ? idx + 1 : null,
            isFirstItem ? sku : null,
            isFirstItem ? productName : null,
            isFirstItem ? estimateDescription : null,
            { text: tieredPricing.qty_from, alignment: 'right' },
            { text: tieredPricing.qty_to, alignment: 'right' },
            {
              text: this.curPipe.transform(tieredPricing.unit_price),
              alignment: 'right',
            },
          ]);
        });

        return;
      }

      const lineItemData = [
        idx + 1,
        sku ?? null,
        productName ?? null,
        { text: estimateDescription ?? null, alignment: 'left' },
        { text: item.quantity, alignment: 'right' },
        {
          text: this.curPipe.transform(
            item.unit_price - item.discount_amount_per_unit,
          ),
          alignment: 'right',
        },
        {
          text: this.curPipe.transform(item.net_total_amount),
          alignment: 'right',
        },
      ];

      if (item.item_type === 'custom') {
        otherLineItemsTableData.push(lineItemData);
      } else {
        lineItemsTableData.push(lineItemData);
      }
    });

    const totalLineItem = [];

    // check if has shipping and tax
    if (quotation.type === 'standard') {
      if (quotation.shipping_amount > 0) {
        totalLineItem.push([
          '',
          '',
          '',
          '',
          '',
          {
            text: 'Shipping',
            alignment: 'right',
            fontSize: 11,
            bold: true,
          },
          {
            text: this.curPipe.transform(quotation.shipping_amount),
            alignment: 'right',
          },
        ]);
      }

      if (quotation.tax_amount > 0) {
        totalLineItem.push([
          '',
          '',
          '',
          '',
          '',
          {
            text: 'Tax',
            alignment: 'right',
            fontSize: 11,
            bold: true,
          },
          {
            text: this.curPipe.transform(quotation.tax_amount),
            alignment: 'right',
          },
        ]);
      }
    }

    totalLineItem.push([
      '',
      '',
      '',
      '',
      '',
      {
        text: 'TOTAL',
        alignment: 'right',
        fontSize: 14,
        bold: true,
      },
      {
        text: this.curPipe.transform(
          totalOverall + quotation.shipping_amount + quotation.tax_amount,
        ),
        alignment: 'right',
        fontSize: 14,
        bold: true,
      },
    ]);

    if (otherLineItemsTableData.length > 1) {
      otherLineItemsTableData.push(...totalLineItem);
    } else if (lineItemsTableData.length > 1) {
      lineItemsTableData.push(...totalLineItem);
    }

    const quotationNumber :string = '#' + quotation.id.toString();

    let shipTo = null;

    if (quotation.client !== null) {
      if (quotation.client.default_shipping) {
        shipTo = quotation.client.default_shipping.fullAddress;
      } else {
        shipTo = quotation.client.shippingFullAddress;
      }
    }

    let billToName = null;

    if (quotation.client.default_billable_user != null) {
      billToName = [
        quotation.client.default_billable_user.firstname,
        quotation.client.default_billable_user.lastname,
      ]
        .filter(Boolean)
        .map((elem) => elem.replace(/\s+/g, ' ').trim())
        .join(' ');
    } else {
      billToName = quotation.client.business_name;
    }

    //Initiate company details from my profile settings
    let companyName :string = '',
      shipFromInvoiceAddress :string = '',
      emailAccounting :string = '',
      companyWebsite :string = '';

    if (quotation?.my_profile_settings) {
      companyName = quotation?.my_profile_settings?.company_name;
      shipFromInvoiceAddress =
        quotation?.my_profile_settings?.ship_from_invoice_address;
      emailAccounting = quotation?.my_profile_settings?.email_accounting;
      companyWebsite = quotation?.my_profile_settings?.website;
    } else {
      this.getMyProfileSettings().subscribe();
      if (this.profileSettings) {
        companyName = this.profileSettings['company_name'];
        shipFromInvoiceAddress = this.profileSettings['ship_from_invoice_address'];
        emailAccounting = this.profileSettings['email_accounting'];
        companyWebsite = this.profileSettings['website'];
      }
    }
    // prepare header
    const headerContent = [
      {
        columns: [
          [
            {
              text: companyName,
              alignment: 'left',
            },
            {
              text: shipFromInvoiceAddress,
              alignment: 'left',
            },
            {
              text: emailAccounting,
              alignment: 'left',
            },
            {
              text: companyWebsite,
              alignment: 'left',
            },
          ],
          [
            {
              text: 'Estimate',
              alignment: 'center',
              style: 'header',
            },
            ...(quotation.client.default_billable_user
              ? [
                  {
                    text: `Attn: ${quotation.client.default_billable_user.firstname} ${quotation.client.default_billable_user.lastname}`,
                    alignment: 'center',
                  },
                ]
              : []),
          ],
          {
            alignment: 'right',
            image: logoUri,
            width: 100,
            style: 'logo',
          },
        ],
      },
      {
        columns: [{ text: '', alignment: '', style: 'header' }],
      },
      {
        columns: [
          [
            { text: 'Bill To', alignment: '', style: 'subheader' },
            { text: '', alignment: '', style: 'subheader' },
            {
              text: billToName,
              alignment: 'left',
            },
            {
              text: quotation?.client?.businessFullAddress,
              alignment: 'left',
            },
            {
              text: quotation?.client?.default_billable_user?.email,
              alignment: 'left',
            },
          ],

          [
            { text: 'Ship To', alignment: '', style: 'subheader' },
            { text: '', alignment: '', style: 'subheader' },
            { text: shipTo != null ? shipTo : '', alignment: 'left' },
            {
              text: quotation?.client?.phone,
              alignment: 'left',
            },
          ],
          [
            { text: 'Details', alignment: '', style: 'subheader' },
            { text: '', alignment: '', style: 'subheader' },
            {
              text: 'Estimate #: ' + quotation.id,
              alignment: 'left',
            },
            {
              text:
                'Expiry Date: ' +
                this.datePipe.transform(
                  quotation.expires_at,
                  dateConfig.defaultDatePipe,
                ),
              alignment: 'left',
            },
            {
              text: 'Payment Term: ' + quotation.payment_term,
              alignment: 'left',
            },
          ],
        ],
      },
    ];

    const deductions = 0;

    const footerContent = [
      {
        columns: [
          [
            {
              columns: [
                {
                  text: quotation?.b2b_portal_settings?.po_display_message,
                  alignment: 'left',
                },
              ],
            },
            {
              columns: [{ text: '', alignment: '', style: 'header' }],
            },
            {
              columns: [{ text: quotation.public_note, alignment: 'left' }],
            },
          ],
          [
            // Placeholder for subtotal and total
          ],
        ],
      },
      {
        columns: [{ text: '', alignment: '', style: 'header' }],
      },
      {
        columns: [
          {
            text: quotation?.b2b_portal_settings?.company_footer,
            alignment: 'center',
          },
        ],
      },
    ];

    const tableContent :any[] = [
      { text: '', alignment: 'center', style: 'header' },
    ];

    // check if table data only contains table headers
    if (tieredPricingLineItemsData.length > 1) {
      tableContent.push({
        style: 'table',
        table: {
          widths: ['auto', 'auto', '*', 'auto', 'auto', 'auto', 'auto'],
          headerRows: 1,
          body: tieredPricingLineItemsData,
        },
        layout: 'headerLineOnly',
      });
    }

    // check if table data only contains table headers
    if (lineItemsTableData.length > 1) {
      tableContent.push({
        style: 'table',
        table: {
          widths: ['auto', '*', 'auto', 'auto', 'auto', 'auto', 'auto'],
          headerRows: 1,
          body: lineItemsTableData,
        },
        layout: 'headerLineOnly',
      });
    }

    if (otherLineItemsTableData.length > 1) {
      tableContent.push({
        style: 'table',
        table: {
          widths: ['auto', '*', 'auto', 'auto', 'auto', 'auto'],
          headerRows: 1,
          body: otherLineItemsTableData,
        },
        layout: 'headerLineOnly',
      });
    }

    let pdfContent :any[] = [];

    pdfContent = pdfContent.concat(headerContent, tableContent, footerContent);

    const documentDefinition = {
      pageSize: 'letter',
      pageOrientation: 'portrait',

      content: pdfContent,

      defaultStyle: {
        fontSize: 10,
        columnGap: 20,
      },
      styles: {
        poheaderright: {
          margin: [0, 25, 0, 0],
          fontSize: 18,
          fillColor: '#00ee00',
        },
        logo: {
          fillColor: '#ee0000',
        },
        summarytitle: {
          fontSize: 14,
          bold: true,
        },
        shipping: {
          margin: [0, 25, 0, 0],
        },
        header: {
          fontSize: 16,
          bold: false,
          margin: [0, 15, 0, 5],
          padding: [0, 0, 0, 0],
        },
        subheader: {
          fontSize: 14,
          bold: false,
          margin: [0, 0, 0, 0],
          padding: [0, 0, 0, 0],
        },
        table: {
          margin: [0, 5, 0, 15],
        },
        tableHeader: {
          bold: true,
          fontSize: 13,
          color: 'black',
        },
        warning: {
          color: '#ffb822',
        },
        info: {
          color: '#5578eb',
        },
        danger: {
          color: '#FF0000',
        },
      },
    };
    Swal.close();
    pdfMake
      .createPdf(documentDefinition)
      .download('Estimate_' + quotationNumber + '.pdf');
  }
}
